import React from 'react';
import {
  Box,
  Typography,
  Pagination,
  Tooltip,
  Button,
  ButtonGroup,
  Select,
  MenuItem
} from '@mui/material';
import { InfoOutlined, MoneyOff } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Table, Tag, Input, Popover, Space, Button as AntButton, Spin, Typography as AntTypography } from 'antd';
import { CaretUpOutlined, CaretDownOutlined } from '@ant-design/icons';
import { colors } from '../../themes/typography';
import { responsiveTypography, responsiveSpacing } from '../../themes/responsive';
import ExperimentTypePill from '../shared/ExperimentTypePill';
import ExperimentStatusIndicator from '../shared/ExperimentStatusIndicator';
import MeasurementStatus, { NEEDS_ACTION } from '../shared/MeasurementStatus';
import FilterPills from './FilterPills';
import SelectionActionBar from './SelectionActionBar';
import CostBreakdownPopover from './CostBreakdownPopover';
import ReplicatePopover from './ReplicatePopover';
import { swapNames } from '../../utils/productShortName';
import TableFilterDropdown from '../shared/TableFilterDropdown';
import useTagFilters from '../../hooks/useTagFilters';
import KPIBox from '../shared/KPIBox';
import 'antd/dist/reset.css';
import dayjs from 'dayjs';
import axios from 'axios';
import { API_BASE_URL, createAuthHeader } from '../../config/api.config';
import { TAG_COLORS, resolveTagColor, DEFAULT_TAG_COLOR } from '../../utils/tagColors';
import { fetchAvailableTags } from '../../services/tagsApi';
import { useAuth } from '../../context/AuthContext';
import useExperimentFilters from '../../hooks/useExperimentFilters';
import { getFilterValues, createFilterDropdown } from './filterConfig';

// Running measurement states, the one that most needs the operator first
const MEASUREMENT_SEVERITY = ['future', 'otherLane', 'notDone', 'inactive', 'waiting', 'measuring'];
// A running experiment's result stays grey until it has this many days of data: a few days of a slow lane say little
const MIN_JUDGE_DAYS = 14;
// Below this many units on both sides (before and now) a sentence would read noise as a result
const MIN_CONCLUDE_UNITS = 4;
// Experiments that change the price of the product they keep: their sentence compares the new price with the old one
const PRICE_TYPES = new Set(['alza_precio_sugerido', 'promo_precio_unitario']);
// Tag color options - using warm Ant Design preset colors

// Unified API function for adding tags to experiments
const addTagsToExperiments = async (experimentIds, tags, clientId, token) => {
  const ids = Array.isArray(experimentIds) ? experimentIds : [experimentIds];
  
  try {
    const response = await axios.post(
      `${API_BASE_URL}/experiments/bulk/tags?clientId=${clientId}`,
      {
        experimentIds: ids,
        tags: tags
      },
      {
        headers: createAuthHeader(token)
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error adding tags:', error);
    throw error;
  }
};

// Unified API function for removing tags from experiments
const removeTagsFromExperiments = async (experimentIds, tags, clientId, token) => {
  const ids = Array.isArray(experimentIds) ? experimentIds : [experimentIds];
  
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/experiments/bulk/tags?clientId=${clientId}`,
      {
        data: {
          experimentIds: ids,
          tags: tags
        },
        headers: createAuthHeader(token)
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error removing tags:', error);
    throw error;
  }
};

// API function to fetch tags from specific experiment IDs
const fetchTagsByExperimentIds = async (experimentIds, clientId, token) => {
  try {
    // Build query string with multiple experimentIds parameters
    const params = new URLSearchParams();
    params.append('clientId', clientId);
    experimentIds.forEach(id => params.append('experimentIds', id));
    
    const response = await axios.get(
      `${API_BASE_URL}/experiments/bulk/tags-by-ids?${params.toString()}`,
      {
        headers: createAuthHeader(token),
        timeout: 10000 // 10 second timeout
      }
    );
    return response.data.tags || [];
  } catch (error) {
    console.error('Error fetching tags by experiment IDs:', error);
    throw error;
  }
};

// Tag Add Content Component - minimal Notion-like design
const TagAddContent = ({ 
  onConfirm, 
  onCancel, 
  existingTags = [],
  availableTags = [],
  loadingTags = false,
  tagsFetchError = null,
  tagColors = TAG_COLORS,
  t
}) => {
  const [inputValue, setInputValue] = React.useState('');
  const [selectedColor, setSelectedColor] = React.useState(
    tagColors[Math.floor(Math.random() * tagColors.length)] || DEFAULT_TAG_COLOR
  );
  const [selectedTags, setSelectedTags] = React.useState([]);
  const [showColorPicker, setShowColorPicker] = React.useState(false);
  const inputRef = React.useRef(null);
  const containerRef = React.useRef(null);
  
  // Combine available tags with a "Create new" option when typing
  const suggestedTags = React.useMemo(() => {
    const filtered = availableTags.filter(tag => 
      !existingTags.some(existing => existing.name.toLowerCase() === tag.name.toLowerCase()) &&
      !selectedTags.some(selected => selected.name.toLowerCase() === tag.name.toLowerCase())
    );
    
    let suggestions = [];
    
    if (inputValue.trim()) {
      const matchingTags = filtered.filter(tag => 
        tag.name.toLowerCase().includes(inputValue.toLowerCase())
      );
      
      const exactMatch = matchingTags.some(tag => 
        tag.name.toLowerCase() === inputValue.toLowerCase()
      );
      
      if (!exactMatch) {
        suggestions.push({
          name: inputValue.trim(),
          color: selectedColor,
          isNew: true
        });
      }
      
      suggestions = [...suggestions, ...matchingTags];
    } else {
      suggestions = filtered;
    }
    
    return suggestions;
  }, [availableTags, existingTags, selectedTags, inputValue, selectedColor]);
  
  React.useEffect(() => {
    // Auto-focus input on mount
    setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
  }, []);
  
  // Handle Enter key to select first suggestion or create new
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      if (suggestedTags.length > 0) {
        handleSelectTag(suggestedTags[0]);
      }
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };
  
  const handleSelectTag = (tag) => {
    setSelectedTags([...selectedTags, tag]);
    setInputValue('');
    // Reset to a new random color for next tag
    setSelectedColor(tagColors[Math.floor(Math.random() * tagColors.length)]);
    inputRef.current?.focus();
  };
  
  const handleRemoveSelectedTag = (tagName) => {
    setSelectedTags(selectedTags.filter(tag => tag.name !== tagName));
  };
  
  const handleConfirmAll = () => {
    if (selectedTags.length > 0) {
      onConfirm(selectedTags);
      onCancel();
    }
  };
  
  // Close on click outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking inside a popover (color picker)
      const isInPopover = event.target.closest('.ant-popover');
      if (containerRef.current && !containerRef.current.contains(event.target) && !isInPopover) {
        onCancel();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onCancel]);
  
  return (
    <div 
      ref={containerRef}
      style={{
        width: 'min(280px, calc(100vw - 24px))',
        backgroundColor: '#ffffff',
        borderRadius: '6px',
        padding: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif'
      }}
    >
      {/* Input area with color picker and Add button */}
      <div style={{ 
        marginBottom: '8px',
        display: 'flex',
        gap: '6px',
        alignItems: 'center',
        position: 'relative'
      }}>
        <div style={{ 
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          position: 'relative'
        }}>
          <Input
            ref={inputRef}
            type="text"
            size="small"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('typeTagAndEnter')}
            style={{ 
              width: '100%',
              paddingLeft: inputValue.trim() ? '32px' : '11px',
              fontSize: '13px',
              borderRadius: '4px',
              backgroundColor: '#f8f9fa',
              border: '1px solid transparent',
              transition: 'all 0.15s ease',
            }}
            onFocus={(e) => {
              e.target.style.backgroundColor = '#ffffff';
              e.target.style.borderColor = '#e0e0e0';
            }}
            onBlur={(e) => {
              e.target.style.backgroundColor = '#f8f9fa';
              e.target.style.borderColor = 'transparent';
            }}
          />
          {/* Color picker dot - only show when typing */}
          {inputValue.trim() && (
            <Popover
              content={
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(5, 1fr)',
                  gap: '8px',
                  padding: '8px'
                }}>
                  {tagColors.map(color => (
                    <div
                      key={color}
                      onClick={() => {
                        setSelectedColor(color);
                        setShowColorPicker(false);
                      }}
                      style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '4px',
                    backgroundColor: resolveTagColor(color),
                        cursor: 'pointer',
                        border: selectedColor === color ? '2px solid #37352f' : '1px solid transparent',
                        transition: 'all 0.15s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    />
                  ))}
                </div>
              }
              trigger="click"
              open={showColorPicker}
              onOpenChange={setShowColorPicker}
              placement="bottomLeft"
            >
              <div
                style={{
                  position: 'absolute',
                  left: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '16px',
                  height: '16px',
                  borderRadius: '3px',
                  backgroundColor: resolveTagColor(selectedColor),
                  cursor: 'pointer',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </Popover>
          )}
        </div>
        <button
          disabled={!inputValue.trim()}
          onClick={() => {
            if (inputValue.trim() && suggestedTags.length > 0) {
              handleSelectTag(suggestedTags[0]);
            }
          }}
          style={{
            minWidth: '50px',
            height: 'auto',
            borderRadius: '4px',
            fontSize: '13px',
            fontWeight: 400,
            backgroundColor: inputValue.trim() ? '#f8f9fa' : '#fafafa',
            border: '1px solid transparent',
            color: inputValue.trim() ? '#37352f' : '#b8b5b0',
            padding: '4px 11px',
            transition: 'all 0.15s ease',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
            cursor: inputValue.trim() ? 'pointer' : 'not-allowed',
            letterSpacing: '-0.01em',
            outline: 'none',
            lineHeight: '1.5715'
          }}
          onMouseEnter={(e) => {
            if (inputValue.trim()) {
              e.currentTarget.style.backgroundColor = '#f0f0f0';
              e.currentTarget.style.borderColor = '#e0e0e0';
            }
          }}
          onMouseLeave={(e) => {
            if (inputValue.trim()) {
              e.currentTarget.style.backgroundColor = '#f8f9fa';
              e.currentTarget.style.borderColor = 'transparent';
            }
          }}
        >
          {t('add') || 'Add'}
        </button>
      </div>
      
      {/* Loading state */}
      {loadingTags && (
        <div style={{ 
          textAlign: 'center', 
          padding: '20px 0',
          color: '#999',
          fontSize: '13px'
        }}>
          {t('loadingTags') || 'Loading tags...'}
        </div>
      )}
      
      {/* Show existing tags when not typing, or filtered results when typing */}
      {!loadingTags && !tagsFetchError && (
        <>
          {/* Show new tag preview when typing something that doesn't exist */}
          {inputValue.trim() && !availableTags.some(tag => tag.name.toLowerCase() === inputValue.trim().toLowerCase()) && (
            <div style={{
              marginBottom: '8px',
              padding: '8px',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
              fontSize: '11px',
              color: '#787774'
            }}>
              <div style={{ marginBottom: '6px' }}>{t('createNewTag') || 'Create new tag:'}</div>
              <Tag
                color={selectedColor}
                style={{
                  fontSize: '12px',
                  fontWeight: 500,
                  margin: 0,
                  padding: '2px 10px',
                  opacity: 0.9
                }}
              >
                {inputValue.trim()}
              </Tag>
            </div>
          )}
          
          {/* Existing tags */}
          {suggestedTags.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              {!inputValue.trim() && (
                <div style={{
                  fontSize: '11px',
                  color: '#787774',
                  marginBottom: '6px'
                }}>
                  {t('availableTags') || t('tags')}
                </div>
              )}
              <div style={{ 
                display: 'flex',
                flexWrap: 'wrap',
                gap: '6px',
                maxHeight: '100px',
                overflowY: 'auto'
              }}>
                {suggestedTags.filter(tag => !tag.isNew).map((tag, index) => (
                  <Tag
                    key={`${tag.name}-${index}`}
                    color={tag.color}
                    style={{
                      fontSize: '11px',
                      fontWeight: 500,
                      margin: 0,
                      padding: '2px 8px',
                      height: '22px',
                      lineHeight: '18px',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                    onClick={() => handleSelectTag(tag)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = '0.8';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = '1';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    {tag.name}
                  </Tag>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      
      
      {/* Selected tags - only show when tags are selected */}
      {selectedTags.length > 0 && (
        <div style={{ 
          marginBottom: '8px',
          paddingTop: '8px',
          borderTop: '1px solid #f0f0f0'
        }}>
          <div style={{ 
            fontSize: '11px', 
            color: '#8c8c8c', 
            marginBottom: '6px' 
          }}>
            {t('selected')} ({selectedTags.length})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'flex-start' }}>
            {selectedTags.map((tag, index) => (
              <Tag
                key={`${tag.name}-${index}`}
                color={tag.color}
                closable
                onClose={() => handleRemoveSelectedTag(tag.name)}
                style={{
                  fontSize: '12px',
                  fontWeight: 500,
                  margin: 0,
                  padding: '0 10px',
                  border: `1px solid ${tag.color}`,
                  borderColor: tag.color
                }}
              >
                {tag.name}
              </Tag>
            ))}
          </div>
        </div>
      )}
      
      {/* Action button - always visible */}
      <AntButton
          type="primary"
          size="small"
          onClick={handleConfirmAll}
          disabled={selectedTags.length === 0}
          style={{ 
            width: '100%', 
            fontSize: '13px',
            height: '32px',
            borderRadius: '4px',
            border: 'none',
            transition: 'all 0.15s ease',
            fontWeight: 500
          }}
        >
          {selectedTags.length > 0
            ? selectedTags.length === 1 
              ? t('add') 
              : `${t('add')} ${selectedTags.length} tags`
            : t('add')
          }
        </AntButton>
    </div>
  );
};

// Unified Tag Popover Component - ensures proper re-rendering when tags are fetched
const TagPopover = ({ 
  children,
  trigger,
  onConfirm,
  onCancel,
  existingTags = [],
  availableTags = [],
  loadingTags = false,
  tagsFetchError = null,
  placement = "bottomLeft",
  open,
  onOpenChange,
  t
}) => {
  // Force re-render when tags or loading state changes
  const contentKey = `${availableTags.length}-${loadingTags}`;
  
  return (
    <Popover
      key={contentKey}
      open={open}
      onOpenChange={onOpenChange}
      trigger={trigger}
      placement={placement}
      overlayStyle={{ paddingTop: '4px' }}
      content={
        <TagAddContent
          onConfirm={onConfirm}
          onCancel={onCancel}
          existingTags={existingTags}
          availableTags={availableTags}
          loadingTags={loadingTags}
          tagsFetchError={tagsFetchError}
              tagColors={TAG_COLORS}
              t={t}
        />
      }
    >
      {children}
    </Popover>
  );
};

// Tag Remove Content Component - handles bulk tag removal
const TagRemoveContent = React.memo(({ 
  selectedRowKeys,
  isSelectAllActive,
  allExperimentIds,
  onConfirm,
  t,
  currentUser
}) => {
  const [availableTagsForRemoval, setAvailableTagsForRemoval] = React.useState([]);
  const [loadingTags, setLoadingTags] = React.useState(true);
  const [selectedForRemoval, setSelectedForRemoval] = React.useState([]);
  
  // Fetch tags when component mounts
  React.useEffect(() => {
    const fetchTags = async () => {
      try {
        setLoadingTags(true);
        const clientId = currentUser?.customer_id;
        const token = localStorage.getItem('access_token');
        
        // Use all experiment IDs if select-all is active, otherwise use selected rows
        const experimentIds = isSelectAllActive && allExperimentIds.length > 0
          ? allExperimentIds
          : selectedRowKeys;

        const tags = await fetchTagsByExperimentIds(experimentIds, clientId, token);
        setAvailableTagsForRemoval(tags);
      } catch (error) {
        console.error('Error fetching tags for removal:', error);
        setAvailableTagsForRemoval([]);
      } finally {
        setLoadingTags(false);
      }
    };
    
    fetchTags();
  }, [selectedRowKeys, isSelectAllActive, allExperimentIds, currentUser]);
  
  const toggleTagSelection = (tag) => {
    setSelectedForRemoval(prev => {
      const isSelected = prev.some(t => t.name === tag.name && t.color === tag.color);
      if (isSelected) {
        return prev.filter(t => !(t.name === tag.name && t.color === tag.color));
      } else {
        return [...prev, tag];
      }
    });
  };
  
  const handleConfirm = () => {
    if (selectedForRemoval.length > 0) {
      onConfirm(selectedForRemoval);
      setSelectedForRemoval([]);
    }
  };
  
  return (
    <Space direction="vertical" size={12} style={{ width: 'min(300px, calc(100vw - 16px))', padding: '8px' }}>
      <div>
        <div style={{ 
          fontSize: '12px', 
          marginBottom: '8px', 
          color: '#787774',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif'
        }}>
          {t('selectTagsToRemove')}
        </div>
        {loadingTags ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100px' 
          }}>
            <Spin size="small" />
          </div>
        ) : availableTagsForRemoval.length === 0 ? (
          <AntTypography.Text type="secondary" style={{ fontSize: '12px' }}>
            {t('noTagsAvailable')}
          </AntTypography.Text>
        ) : (
          <div style={{ 
            maxHeight: '280px', 
            overflowY: 'auto',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px'
          }}>
            {availableTagsForRemoval.map((tag) => (
              <Tag
                key={`${tag.name}-${tag.color}`}
                color={tag.color || 'blue'}
                closable={false}
                style={{
                  fontSize: '11px',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                  fontWeight: 500,
                  margin: 0,
                  cursor: 'pointer',
                  opacity: selectedForRemoval.some(t => t.name === tag.name && t.color === tag.color) ? 1 : 0.5,
                  border: selectedForRemoval.some(t => t.name === tag.name && t.color === tag.color) ? '2px solid #ff4d4f' : 'none',
                  transition: 'all 0.2s'
                }}
                onClick={() => toggleTagSelection(tag)}
              >
                {tag.name}
              </Tag>
            ))}
          </div>
        )}
      </div>
      <AntButton
        type="primary"
        danger
        size="small"
        onClick={handleConfirm}
        disabled={selectedForRemoval.length === 0}
        style={{ width: '100%', fontSize: '12px' }}
      >
        {t('removeSelectedTags')} ({selectedForRemoval.length})
      </AntButton>
    </Space>
  );
});

// Tag Cell Component - renders tags with add/remove functionality for each row
const TagCell = ({
  tags,
  recordKey,
  experimentIds: experimentIdsProp,
  editingTags,
  setEditingTags,
  availableTags,
  loadingTags,
  tagsFetchError,
  fetchTags,
  refreshFilters,
  t,
  currentUser,
  showToast
}) => {
  const currentTags = editingTags[recordKey] || tags || [];
  const [inputVisible, setInputVisible] = React.useState(false);
  
  // For slot groups, use child experiment IDs; otherwise use recordKey
  const apiIds = experimentIdsProp || recordKey;

  const handleClose = React.useCallback(async (removedTag) => {
    try {
      const clientId = currentUser?.customer_id;
      const token = localStorage.getItem('access_token');

      const response = await removeTagsFromExperiments(apiIds, [removedTag], clientId, token);

      if (response.results?.succeeded?.length > 0) {
        const allTags = [];
        const seen = new Set();
        const updates = {};
        response.results.succeeded.forEach(({ experimentId, tags: t2 }) => {
          updates[experimentId] = t2;
          t2.forEach(tag => {
            const tagKey = `${tag.name}-${tag.color}`;
            if (!seen.has(tagKey)) { seen.add(tagKey); allTags.push(tag); }
          });
        });
        updates[recordKey] = allTags;
        setEditingTags(prev => ({ ...prev, ...updates }));
        refreshFilters();
      } else if (response.results?.failed?.length > 0) {
        showToast(t('failedToRemoveTag'), 'error');
      }
    } catch (error) {
      console.error('Error removing tag:', error);
      showToast(t('errorRemovingTag'), 'error');
    }
  }, [apiIds, recordKey, setEditingTags, refreshFilters, t, showToast, currentUser]);
  
  const handleInputConfirm = React.useCallback(async (tagsOrName, tagColor) => {
    // Handle both single tag (for backward compatibility) and array of tags
    const tagsToAdd = Array.isArray(tagsOrName) ? tagsOrName : [{ name: tagsOrName, color: tagColor || 'blue' }];
    
    // Filter out any tags that already exist
    const newTagsToAdd = tagsToAdd.filter(newTag => 
      !currentTags.some(tag => tag.name === newTag.name)
    );
    
    if (newTagsToAdd.length > 0) {
      try {
        const clientId = currentUser?.customer_id;
        const token = localStorage.getItem('access_token');

        const response = await addTagsToExperiments(apiIds, newTagsToAdd, clientId, token);

        if (response.results?.succeeded?.length > 0) {
          // Merge all succeeded tags as the aggregate for the recordKey
          const allTags = [];
          const seen = new Set();
          const updates = {};
          response.results.succeeded.forEach(({ experimentId, tags: t2 }) => {
            updates[experimentId] = t2;
            t2.forEach(tag => {
              const tagKey = `${tag.name}-${tag.color}`;
              if (!seen.has(tagKey)) { seen.add(tagKey); allTags.push(tag); }
            });
          });
          updates[recordKey] = allTags;
          setEditingTags(prev => ({ ...prev, ...updates }));
          refreshFilters();
        } else if (response.results?.failed?.length > 0) {
          showToast(t('failedToAddTags'), 'error');
        }
      } catch (error) {
        console.error('Error adding tags:', error);
        showToast(t('errorAddingTags'), 'error');
      }
    }
    setInputVisible(false);
  }, [currentTags, apiIds, recordKey, setEditingTags, refreshFilters, t, showToast, currentUser]);
  
  const showInput = React.useCallback(() => {
    setInputVisible(true);
    // Fetch tags when opening individual tag input
    if (fetchTags) {
      fetchTags();
    }
  }, [fetchTags]);
  
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
      {currentTags.map((tag, index) => (
        <Tag
          key={`${tag.name}-${index}`}
          closable
          color={tag.color || 'blue'}
          onClose={() => handleClose(tag)}
          style={{
            fontSize: '11px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
            fontWeight: 500,
            margin: 0
          }}
        >
          {tag.name}
        </Tag>
      ))}
      {inputVisible ? (
        <TagPopover
          open={inputVisible}
          onOpenChange={(visible) => {
            if (!visible) {
              setInputVisible(false);
            }
          }}
          trigger="click"
          placement="bottomLeft"
          onConfirm={handleInputConfirm}
          onCancel={() => {
            setInputVisible(false);
          }}
          existingTags={currentTags}
          availableTags={availableTags}
          loadingTags={loadingTags}
          tagsFetchError={tagsFetchError}
          t={t}
        >
          <Tag
            style={{
              fontSize: '11px',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
              fontWeight: 500,
              margin: 0,
              padding: '0 8px',
              height: '20px',
              lineHeight: '18px',
              borderRadius: '10px',
              backgroundColor: '#f5f5f5',
              border: '1px dashed #d9d9d9',
              color: '#595959',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#40a9ff';
              e.currentTarget.style.color = '#40a9ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#d9d9d9';
              e.currentTarget.style.color = '#595959';
            }}
          >
            +
          </Tag>
        </TagPopover>
      ) : (
        <Tag
          onClick={showInput}
          style={{
            fontSize: '11px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
            fontWeight: 500,
            margin: 0,
            padding: '0 8px',
            height: '20px',
            lineHeight: '18px',
            borderRadius: '10px',
            backgroundColor: '#f5f5f5',
            border: '1px dashed #d9d9d9',
            color: '#595959',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#40a9ff';
            e.currentTarget.style.color = '#40a9ff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#d9d9d9';
            e.currentTarget.style.color = '#595959';
          }}
        >
          +
        </Tag>
      )}
    </div>
  );
};

// Add display name for React DevTools
TagCell.displayName = 'TagCell';

const ExperimentsTable = ({
  data = [],
  totalCount = 0,
  totalExperimentsCount = 0,
  currentPage = 1,
  totalPages = 1,
  pageSize = 10,
  loading = false,
  selectedBaseline = 'historical',
  onBaselineChange,
  viewMode = 'unit',
  onViewModeChange,
  onPageChange,
  onFiltersChange,
  showToast,
  totals,
  allExperimentIds = [],
  showKPIComparisonHint = true,
  initialFilters
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // Calculate default date range (last 3 months)
  const getDefaultDateRange = () => {
    const today = dayjs();
    const threeMonthsAgo = today.subtract(3, 'month');
    return [threeMonthsAgo.format('DD/MM/YYYY'), today.format('DD/MM/YYYY')];
  };

  // Filter state - initialize with last 3 months date filter and running status
  const [filteredInfo, setFilteredInfo] = React.useState({
    dynamicDateRange: getDefaultDateRange(),
    status: ['running'], // Default to show only running experiments
    ...initialFilters
  });
  
  // Sort state - default to delta profit descending (positive deltas first)
  const [sortedInfo, setSortedInfo] = React.useState({
    columnKey: 'dynamicDeltaGrossProfit',
    order: 'descend'
  });
  
  // Dynamic filters hook for faceted search
  const { filterData, loading: filtersLoading, error: filtersError, refetchFilters } = useExperimentFilters(
    currentUser?.customer_id,
    filteredInfo
  );
  
  // State for available tags
  const [availableTags, setAvailableTags] = React.useState([]);
  const [loadingTags, setLoadingTags] = React.useState(false);
  const [tagsFetchError, setTagsFetchError] = React.useState(null);
  
  // Initialize tag filters hook
  const { tagFilters, refreshFilters, allTags } = useTagFilters({
    clientId: currentUser?.customer_id,
    filteredInfo,
    filterData
  });
  
  
  // Bulk tag popover state - moved up to be available for fetchTags
  const [bulkTagPopoverOpen, setBulkTagPopoverOpen] = React.useState(false);
  const [bulkRemoveTagsOpen, setBulkRemoveTagsOpen] = React.useState(false);
  
  // Ref to track if tags are currently being fetched (prevents concurrent fetches)
  const fetchingRef = React.useRef(false);
  
  
  // Function to fetch available tags - always fetches fresh data
  const fetchTags = React.useCallback(async () => {
    if (Array.isArray(allTags) && allTags.length > 0) {
      setAvailableTags(allTags);
      setTagsFetchError(null);
      setLoadingTags(false);
      return;
    }

    if (fetchingRef.current) {
      return; // Avoid duplicate fetches while loading
    }
    
    fetchingRef.current = true;
    setLoadingTags(true);
    setTagsFetchError(null);
    
    try {
      const clientId = currentUser?.customer_id;
      const token = localStorage.getItem('access_token');
      
      if (clientId && token) {
        const tags = await fetchAvailableTags(clientId, token);
        setAvailableTags(tags);
      }
    } catch (error) {
      console.error('Failed to fetch tags:', error);
      setTagsFetchError(error);
      // Set empty array on error so users can still create new tags
      setAvailableTags([]);
    } finally {
      setLoadingTags(false);
      fetchingRef.current = false;
    }
  }, [currentUser?.customer_id, allTags]);
  
  
  // Fetch fresh tags every time bulk tag popover opens
  React.useEffect(() => {
    if (bulkTagPopoverOpen) {
      fetchTags();
    }
  }, [bulkTagPopoverOpen, fetchTags]);

  React.useEffect(() => {
    if (Array.isArray(allTags)) {
      setAvailableTags(allTags);
      if (allTags.length > 0) {
        setLoadingTags(false);
        setTagsFetchError(null);
      }
    }
  }, [allTags]);
  
  
  
  
  // Tags editing state - simplified to just store the tags data
  const [editingTags, setEditingTags] = React.useState({});
  // The experiment whose swap is being tried in more machines, and the link it opens at (ReplicatePopover)
  const [replicate, setReplicate] = React.useState(null);
  
  // Row selection state
  const [selectedRowKeys, setSelectedRowKeys] = React.useState([]);
  const [isSelectAllActive, setIsSelectAllActive] = React.useState(false);
  
  // Track scroll state for sticky header shadow
  const [isScrolled, setIsScrolled] = React.useState(false);

  // Use the totalExperimentsCount prop directly for the display
  // For unit view, this equals totalCount; for grouped views, it's the actual total experiments across all pages
  const displayExperimentsCount = totalExperimentsCount || totalCount;

  // Track hovered row for checkbox visibility
  const [hoveredRowKey, setHoveredRowKey] = React.useState(null);

  // Expandable rows state for grouped mode
  const [expandedRowKeys, setExpandedRowKeys] = React.useState([]);
  const [expandedRowData, setExpandedRowData] = React.useState({});
  // Expandable rows state for slot groups in unit mode
  const [slotExpandedKeys, setSlotExpandedKeys] = React.useState([]);

  // When data changes (page change) and select-all is active, add all rows from new page to selection
  React.useEffect(() => {
    if (isSelectAllActive && data && data.length > 0) {
      const currentPageKeys = data.map(item => item.key || item.id);
      // Merge with existing selections from other pages
      setSelectedRowKeys(prevKeys => {
        const existingKeys = new Set(prevKeys);
        const newKeys = currentPageKeys.filter(key => !existingKeys.has(key));
        return [...prevKeys, ...newKeys];
      });
    }
  }, [data, isSelectAllActive]);
  
  // Detect scroll to add shadow to sticky header
  React.useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Map sort column keys when view mode changes to ensure consistent sorting UI
  // Unit view uses 'dynamicDelta*' keys, grouped views use 'delta_*' keys
  // This only updates local state for UI consistency (sort indicators)
  // The actual sort key mapping for API calls is handled in useExperimentTableData hook
  React.useEffect(() => {
    const unitToGroupedMapping = {
      'dynamicDelta': 'delta_units',
      'dynamicDeltaEuros': 'delta_revenue',
      'dynamicDeltaGrossProfit': 'delta_grossProfit'
    };

    const groupedToUnitMapping = {
      'delta_units': 'dynamicDelta',
      'delta_revenue': 'dynamicDeltaEuros',
      'delta_grossProfit': 'dynamicDeltaGrossProfit'
    };

    if (viewMode === 'unit') {
      // Switching to unit view - map grouped keys to unit keys for UI
      if (sortedInfo.columnKey && groupedToUnitMapping[sortedInfo.columnKey]) {
        const newKey = groupedToUnitMapping[sortedInfo.columnKey];
        setSortedInfo(prev => ({ ...prev, columnKey: newKey }));
      }
    } else {
      // Switching to grouped view (group or route) - map unit keys to grouped keys for UI
      if (sortedInfo.columnKey && unitToGroupedMapping[sortedInfo.columnKey]) {
        const newKey = unitToGroupedMapping[sortedInfo.columnKey];
        setSortedInfo(prev => ({ ...prev, columnKey: newKey }));
      }
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps


  // Wrapper components to pass required props to FilterPills
  const BoundTagAddContent = React.useCallback((props) => (
    <TagAddContent
      {...props}
      availableTags={availableTags}
      tagColors={TAG_COLORS}
      loadingTags={loadingTags}
      tagsFetchError={tagsFetchError}
      t={t}
    />
  ), [availableTags, loadingTags, tagsFetchError, t]);
  
  // Baseline comparison mode state
  const [tableBaselineMode, setTableBaselineMode] = React.useState(selectedBaseline || 'historical');

  // Update table baseline when prop changes
  React.useEffect(() => {
    if (selectedBaseline) {
      setTableBaselineMode(selectedBaseline);
    }
  }, [selectedBaseline]);

  const handleBaselineChange = (newMode) => {
    setTableBaselineMode(newMode);
    if (onBaselineChange) {
      onBaselineChange(newMode);
    }
  };

  // Helper function for clickable store ID rendering
  const renderClickableStoreId = (storeId, record) => {
    const handleStoreIdClick = () => {
      if (record.store?.group && record.store?.location) {
        navigate('/machines', {
          state: {
            defaultTab: 'planograma',
            filters: {
              group: record.store.group,
              location: record.store.location
            }
          }
        });
      }
    };

    return (
      <Tooltip title={`${record.store?.group || ''} - ${record.store?.location || ''}`} arrow placement="top">
        <Typography
          component="span"
          onClick={handleStoreIdClick}
          sx={{
            color: '#787774', // Notion warm gray
            textDecoration: 'none',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: 400,
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
            letterSpacing: '-0.01em',
            '&:hover': {
              color: '#37352f',
              textDecoration: 'underline'
            }
          }}
        >
          {storeId || '-'}
        </Typography>
      </Tooltip>
    );
  };

  // Format date range for display
  const formatDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    const startDay = start.getDate();
    const endDay = end.getDate();
    
    // Get translated month names
    const monthNames = [
      t('january'), t('february'), t('march'), t('april'), t('may'), t('june'),
      t('july'), t('august'), t('september'), t('october'), t('november'), t('december')
    ];
    
    const startMonthIndex = start.getMonth();
    const endMonthIndex = end.getMonth();
    const startMonth = monthNames[startMonthIndex].slice(0, 3); // Get first 3 characters
    const endMonth = monthNames[endMonthIndex].slice(0, 3);
    
    // The year only when it is not this one ("Oct 12-Abr 12 2027"), on the start only if it differs from the end's
    const thisYear = new Date().getFullYear();
    const endYear = end.getFullYear() !== thisYear ? ` ${end.getFullYear()}` : '';
    const startYear = start.getFullYear() !== thisYear && start.getFullYear() !== end.getFullYear() ? ` ${start.getFullYear()}` : '';

    // If same month, show "Jul 1-15"
    if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
      return `${startMonth} ${startDay}-${endDay}${endYear}`;
    }
    
    // If different months, show "Jul 25-Aug 8"
    return `${startMonth} ${startDay}${startYear}-${endMonth} ${endDay}${endYear}`;
  };

  const renderRoutes = (routes = []) => {
    if (!Array.isArray(routes) || routes.length === 0) {
      return (
        <span
          style={{
            fontSize: '12px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
            fontWeight: 400,
            color: '#787774',
            letterSpacing: '-0.01em'
          }}
        >
          -
        </span>
      );
    }

    const names = routes
      .map((route) => route?.name || route?.code || String(route?.id ?? ''))
      .filter(Boolean);
    if (names.length === 0) {
      return (
        <span
          style={{
            fontSize: '12px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
            fontWeight: 400,
            color: '#787774',
            letterSpacing: '-0.01em'
          }}
        >
          -
        </span>
      );
    }

    const displayText = names.slice(0, 2).join(', ');
    const tooltipText = names.join(', ');

    return (
      <Tooltip title={tooltipText} arrow placement="top">
        <span
          style={{
            fontSize: '12px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
            fontWeight: 400,
            color: '#37352f',
            letterSpacing: '-0.01em'
          }}
        >
          {displayText}
          {names.length > 2 ? '…' : ''}
        </span>
      </Tooltip>
    );
  };

  // Helper function to parse combo name and extract second product
  const parseComboName = (comboName) => {
    // Pattern: "COMBO PRODUCT1 + PRODUCT2"
    const match = comboName?.match(/^COMBO\s+(.+?)\s*\+\s*(.+)$/);
    if (match) {
      return {
        firstProduct: match[1].trim(),
        secondProduct: match[2].trim()
      };
    }
    return null;
  };


  // Helper function to truncate text
  const truncateText = (text, maxLength = 27) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Slot badge for products column
  const renderSlotBadge = (record) => {
    if (!record.slots || record.slots.length === 0) return null;
    const label = `${record.slots.length} ${record.slots.length === 1 ? 'carril' : 'carriles'}`;
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '2px',
        marginLeft: '6px',
        padding: '1px 6px',
        borderRadius: '999px',
        backgroundColor: '#EFF6FF',
        color: '#3B82F6',
        fontSize: '10px',
        fontWeight: 600,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
        whiteSpace: 'nowrap',
        verticalAlign: 'middle'
      }}>
        {label}
      </span>
    );
  };

  // Format products based on experiment type
  const formatProducts = (record) => {
    const { type, product } = record;
    const originalProduct = product?.original || '';
    const newProduct = product?.new || '';
    const slotBadge = renderSlotBadge(record);
    const slotIds = record.slots?.length > 0 ? record.slots.map(s => s.selectionId).join(', ') : null;
    const buildTooltip = (mainText) => slotIds
      ? <span>{mainText}<br/>{t('slots', { defaultValue: 'Carriles' })}: {slotIds}</span>
      : mainText;

    // Slot group parent: show expand arrow + product display
    if (record.isSlotGroup) {
      const isExpanded = slotExpandedKeys.includes(record.key);
      const childCount = record.slotChildren?.length || 0;

      let normalContent;
      if (childCount === 1) {
        // Single child: render as if it were that child (full product display with → or +)
        const displayRecord = { ...record.slotChildren[0], slots: undefined };
        normalContent = formatProducts(displayRecord);
      } else {
        // Multiple children: show only original product + "N exp" badge
        const originalProduct = record.product?.original || '';
        const truncated = truncateText(originalProduct);
        normalContent = (
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            <Tooltip title={originalProduct} enterDelay={500} arrow>
              <span style={{
                fontSize: '12px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                fontWeight: 400,
                color: '#37352f',
                letterSpacing: '-0.01em',
                cursor: 'help'
              }}>
                {truncated}
              </span>
            </Tooltip>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              marginLeft: '6px',
              padding: '1px 6px',
              borderRadius: '999px',
              backgroundColor: '#F5F0E8',
              color: '#9B8E7B',
              fontSize: '10px',
              fontWeight: 600,
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
              whiteSpace: 'nowrap',
              verticalAlign: 'middle'
            }}>
              {childCount} exp
            </span>
          </span>
        );
      }

      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <span className="slot-expand-chevron" style={{
            fontSize: '21px',
            lineHeight: 1,
            color: '#c4c4c0',
            transition: 'transform 150ms ease, color 150ms ease',
            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
            display: 'inline-block',
            flexShrink: 0,
            position: 'relative',
            top: '-2px',
          }}>›</span>
          {normalContent}
        </span>
      );
    }

    switch (type) {
      case 'promo_precio_unitario': {
        // For discounts, show only the product name
        const truncated = truncateText(originalProduct);

        const content = (
          <span style={{
            fontSize: '12px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
            fontWeight: 400,
            color: '#37352f',
            letterSpacing: '-0.01em',
            cursor: 'help'
          }}>
            {truncated}
          </span>
        );

        // Always show tooltip for consistency
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            <Tooltip title={buildTooltip(originalProduct)} enterDelay={500} arrow>
              {content}
            </Tooltip>
            {slotBadge}
          </span>
        );
      }

      case 'ampliar_carril':
      case 'reemplazo_producto': {
        // For replacements and lane expansions, show Product1 → Product2
        const fullText = `${originalProduct} → ${newProduct}`;
        const truncatedOriginal = truncateText(originalProduct, 12);
        const truncatedNew = truncateText(newProduct, 12);

        const content = (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'help' }}>
            <span style={{
              fontSize: '12px',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
              fontWeight: 400,
              color: '#37352f',
              letterSpacing: '-0.01em'
            }}>
              {truncatedOriginal}
            </span>
            <span style={{ fontSize: '11px', color: '#787774' }}>→</span>
            <span style={{
              fontSize: '12px',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
              fontWeight: 400,
              color: '#37352f',
              letterSpacing: '-0.01em'
            }}>
              {truncatedNew}
            </span>
          </Box>
        );

        // Always show tooltip for consistency
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            <Tooltip title={buildTooltip(fullText)} enterDelay={500} arrow>
              {content}
            </Tooltip>
            {slotBadge}
          </span>
        );
      }

      case 'promo_combo_combinado': {
        // For combos, parse the combo name to extract the second product
        const parsed = parseComboName(newProduct);
        const secondProduct = parsed?.secondProduct || newProduct;
        const fullText = `${originalProduct} + ${secondProduct}`;
        const truncatedOriginal = truncateText(originalProduct, 12);
        const truncatedSecond = truncateText(secondProduct, 12);

        const content = (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'help' }}>
            <span style={{
              fontSize: '12px',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
              fontWeight: 400,
              color: '#37352f',
              letterSpacing: '-0.01em'
            }}>
              {truncatedOriginal}
            </span>
            <span style={{ fontSize: '11px', color: '#787774' }}>+</span>
            <span style={{
              fontSize: '12px',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
              fontWeight: 400,
              color: '#37352f',
              letterSpacing: '-0.01em'
            }}>
              {truncatedSecond}
            </span>
          </Box>
        );

        // Always show tooltip for consistency
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            <Tooltip title={buildTooltip(fullText)} enterDelay={500} arrow>
              {content}
            </Tooltip>
            {slotBadge}
          </span>
        );
      }

      default:
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            {originalProduct || '-'}
            {slotBadge}
          </span>
        );
    }
  };

  // Format delta values with proper symbols and colors
  const formatDelta = (value, type = 'units', hasBaseline = true, experimentType = null, muted = false) => {
    // Handle null/undefined values
    if (value === null || value === undefined) {
      // If no baseline data, show info icon with tooltip
      if (!hasBaseline) {
        return (
          <Tooltip title={t('noDataForPeriod')} arrow placement="top">
            <InfoOutlined 
              sx={{ 
                fontSize: '14px',
                color: '#787774',
                cursor: 'help',
                verticalAlign: 'middle'
              }} 
            />
          </Tooltip>
        );
      }
      // For accepted experiments or other cases, show dash
      return (
        <span style={{ 
          color: '#b8b5b0', 
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
          fontWeight: 400,
          fontSize: '13px',
          letterSpacing: '-0.01em'
        }}>
          -
        </span>
      );
    }

    const roundsToZero = type === 'euros' ? Math.abs(value) < 0.005 : Math.round(Math.abs(value)) === 0;
    if (roundsToZero) {
      return (
        <span style={{
          color: '#787774',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
          fontWeight: 500,
          fontSize: '13px',
          letterSpacing: '-0.01em'
        }}>
          {type === 'euros' ? '€0.00' : '0'}
        </span>
      );
    }

    const isPositive = value >= 0;
    const symbol = isPositive ? '+' : '-';
    // A price increase is expected to lose units, so a negative units delta is not a bad outcome: show it grey
    const isExpectedUnitsDrop = experimentType === 'alza_precio_sugerido' && type === 'units' && value < 0;
    const color = isExpectedUnitsDrop || muted
      ? '#787774' // Neutral grey
      : (isPositive ? '#448361' : '#d9534f'); // Warm green for positive, warm red for negative

    let formattedValue;
    switch (type) {
      case 'euros':
        formattedValue = `${symbol}€${Math.abs(value).toFixed(2)}`;
        break;
      case 'percentage':
        formattedValue = `${symbol}${Math.round(Math.abs(value))}%`;
        break;
      case 'units':
      default:
        formattedValue = `${symbol}${Math.round(Math.abs(value))}`;
        break;
    }
    
    return (
      <span style={{ 
        color, 
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
        fontWeight: 500,
        fontSize: '13px',
        letterSpacing: '-0.01em'
      }}>
        {formattedValue}
      </span>
    );
  };

  // Get consolidated filter values using centralized configuration
  const filterValues = React.useMemo(() => {
    return getFilterValues(filterData, data);
  }, [filterData, data]);

  // Create tag filter dropdown renderer outside of columns to avoid stale closures
  const renderTagFilterDropdown = React.useCallback((props) => {
    // Priority order: 1) Cache, 2) API filterValues, 3) Extract from data
    let tagValues = [];
    let source = '';
    
    if (tagFilters.length > 0) {
      // Use tag filters (prevents table refresh)
      tagValues = tagFilters.map(tagName => ({
        text: tagName,
        value: tagName
      }));
      source = 'tagFilters (cached)';
    } else if (filterValues?.tags && filterValues.tags.length > 0) {
      // Use API-provided tags from filterData
      tagValues = filterValues.tags.map(tagName => ({
        text: tagName,
        value: tagName
      }));
      source = 'filterValues (from API)';
    } else {
      // Fallback to extracting from data
      const allTags = new Set();
      data.forEach(item => {
        const tags = editingTags[item.id || item.key] || item.tags || [];
        tags.forEach(tag => {
          if (tag.name) {
            allTags.add(tag.name);
          }
        });
      });
      
      tagValues = Array.from(allTags).sort().map(tagName => ({
        text: tagName,
        value: tagName
      }));
      source = 'extracted from table data';
    }
    
    return (
      <TableFilterDropdown
        type="checkbox"
        values={tagValues}
        columnName={t('tags')}
        loading={filtersLoading}
        {...props}
      />
    );
  }, [tagFilters, filterValues, data, editingTags, t, filtersLoading]);

  // Custom sort icon renderer
  const renderSortIcon = ({ sortOrder }) => (
    <span style={{ 
      display: 'inline-flex', 
      flexDirection: 'column',
      marginLeft: '4px',
      fontSize: '10px',
      lineHeight: '0.5',
      verticalAlign: 'middle'
    }}>
      <CaretUpOutlined 
        style={{ 
          color: sortOrder === 'ascend' ? '#1890ff' : '#bfbfbf',
          marginBottom: '-2px',
          opacity: sortOrder === 'ascend' ? 1 : 0.5
        }} 
      />
      <CaretDownOutlined 
        style={{ 
          color: sortOrder === 'descend' ? '#1890ff' : '#bfbfbf',
          marginTop: '-2px',
          opacity: sortOrder === 'descend' ? 1 : 0.5
        }} 
      />
    </span>
  );

  const fmtEurosValue = (v) => (v == null ? '–' : `${v < 0 ? '-' : ''}€${Math.abs(v).toFixed(2)}`);

  // Negative because it sells below its cost: almost always a wrong price on the machine
  const sellsBelowCost = (record) => {
    const perf = record.performance || {};
    const delta = record.comparisons?.[tableBaselineMode]?.delta?.grossProfit;
    return !record.measurement?.freeVend && delta != null && delta < 0 && perf.units > 0 && perf.revenue < (perf.totalCost || 0);
  };

  /**
   * One number per experiment: its gross profit against what the lane earned before over the same days (units on a
   * free-vend machine, where euros say nothing). Running ones show it from day one, grey until there are enough days
   * to judge. A click opens how it is built: units, price, revenue, each tax, the canon and the supplier cost.
   */
  const renderResult = (record) => {
    const comparison = record.comparisons?.[tableBaselineMode];
    const m = record.measurement;
    if (comparison?.metadata?.hasData === false) {
      return (
        <Tooltip title={t('noComparisonDataDeltaExplanation')} arrow placement="top">
          <span style={{ cursor: 'help' }}>{formatDelta(null)}</span>
        </Tooltip>
      );
    }
    const freeVend = Boolean(m?.freeVend);
    const value = freeVend ? comparison?.delta?.units : comparison?.delta?.grossProfit;
    // Not started, or not counting yet: the status cell says why
    if (record.status === 'accepted' || (m && !m.since) || value == null) return formatDelta(null);

    const early = Boolean(m) && m.dataDays < MIN_JUDGE_DAYS;
    const number = (
      <span style={{ whiteSpace: 'nowrap' }}>
        {formatDelta(value, freeVend ? 'units' : 'euros', true, null, early)}
        {freeVend && (
          <Tooltip title={t('ms_freeVendHint')} arrow placement="top">
            <span style={{ color: '#787774', fontSize: '12px', cursor: 'help' }}> {t('res_unitsShort')}</span>
          </Tooltip>
        )}
      </span>
    );
    const experimentIds = record.isSlotGroup ? (record.slotChildren || []).map((c) => c.id) : [record.id];
    const cell = experimentIds.some((id) => !id) ? number : (
      <CostBreakdownPopover experimentIds={experimentIds} customerId={currentUser?.customer_id} baseline={tableBaselineMode}>
        {number}
      </CostBreakdownPopover>
    );
    if (!sellsBelowCost(record)) return cell;
    const perf = record.performance;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        <Tooltip
          title={t('res_belowCost', { price: fmtEurosValue(perf.revenue / perf.units), cost: fmtEurosValue(perf.totalCost / perf.units) })}
          arrow
          placement="top"
        >
          <MoneyOff sx={{ fontSize: 15, color: '#d9534f', cursor: 'help' }} />
        </Tooltip>
        {cell}
      </span>
    );
  };

  // A group's result: its experiments' gross profit against before; a click opens how it is built, all of them added up
  const renderGroupResult = (record) => {
    const number = formatDelta(record.delta_grossProfit, 'euros', record.hasBaselineData !== false);
    if (!record.experimentIds?.length) return number;
    return (
      <CostBreakdownPopover experimentIds={record.experimentIds} customerId={currentUser?.customer_id} baseline={tableBaselineMode}>
        {number}
      </CostBreakdownPopover>
    );
  };

  // What the sentence compares: the new product with the one it replaced, the new price with the old one, or the lane
  // with itself before
  const comparedTerms = (record, comparison) => {
    const product = record.product || {};
    // A machine's lanes added up have no single new product (product.new is cleared), so they fall to "before"
    if (record.type === 'reemplazo_producto' && product.original && product.new) {
      const [from, to] = swapNames(product.original, product.new);
      if (from !== to) return { a: to, b: from, swap: true };
    }
    const perf = record.performance || {};
    const baseline = comparison?.baseline || {};
    const priceNow = perf.units > 0 ? perf.revenue / perf.units : null;
    const priceBefore = baseline.units > 0 ? baseline.revenue / baseline.units : null;
    if (PRICE_TYPES.has(record.type) && priceNow != null && priceBefore != null && Math.abs(priceNow - priceBefore) >= 0.01) {
      return { a: t('cn_atPrice', { price: fmtEurosValue(priceNow) }), b: t('cn_priceRef', { price: fmtEurosValue(priceBefore) }) };
    }
    const lever = record.slotChildren?.length > 1 ? 'cn_withChanges' : record.type === 'ampliar_carril' ? 'cn_withLane' : 'cn_withChange';
    return { a: t(lever), b: t('cn_before') };
  };

  /**
   * What the experiment says, in one sentence naming what it compares ("Monster vende más que Café Frío y con más
   * margen"): how its units and gross profit moved against before (the same days while it runs), and whether the
   * margin per unit explains it. Grey when it cannot say yet.
   */
  const conclusionKey = (record) => {
    const comparison = record.comparisons?.[tableBaselineMode];
    const m = record.measurement;
    const perf = record.performance || {};
    const baseline = comparison?.baseline || {};
    const delta = comparison?.delta || {};
    // Not started, or not counting: the status dot says why
    if (record.status === 'accepted' || (m && (!m.since || NEEDS_ACTION.has(m.state)))) return { key: null };
    if (comparison?.metadata?.hasData === false) return { key: 'cn_noBefore', muted: true };
    if (delta.grossProfit == null) return { key: null };
    const terms = comparedTerms(record, comparison);
    // A price below cost is a fact from the first sale, not a trend that needs days
    if (sellsBelowCost(record)) return { key: 'cn_belowCost', values: terms };
    if (m && m.dataDays < MIN_JUDGE_DAYS) return { key: 'cn_tooEarly', muted: true };
    if (Math.max(baseline.units || 0, perf.units || 0) < MIN_CONCLUDE_UNITS) return { key: 'cn_fewSales', muted: true };
    if (comparison?.metadata?.zeroBaseline) {
      return terms.swap ? { key: 'cn_zeroSwap', values: { old: terms.b, new: terms.a } } : { key: 'cn_zeroBefore' };
    }

    // -1, 0 or 1: down, about the same, up
    const move = (change, band) => (change >= band ? 1 : change <= -band ? -1 : 0);
    const units = move(delta.units, Math.max(2, 0.1 * (baseline.units || 0)));
    if (m?.freeVend) return { key: { 1: 'cn_more', 0: 'cn_same', [-1]: 'cn_less' }[units], values: terms };
    const profit = move(delta.grossProfit, Math.max(1, 0.05 * Math.abs(baseline.grossProfit || 0)));
    const marginBefore = baseline.units > 0 ? baseline.grossProfit / baseline.units : null;
    const marginNow = perf.units > 0 ? perf.grossProfit / perf.units : null;
    const margin = marginBefore == null || marginNow == null
      ? 0 : move(marginNow - marginBefore, Math.max(0.02, 0.05 * Math.abs(marginBefore)));
    const byMargin = (up, same, down) => (margin > 0 ? up : margin < 0 ? down : same);
    const key = {
      '1,1': byMargin('cn_moreBetterMargin', 'cn_moreSameMargin', 'cn_moreVolumeCompensates'),
      '1,0': 'cn_sameBetterMargin',
      '1,-1': 'cn_lessMarginCompensates',
      '0,1': 'cn_moreSameProfit',
      '0,0': 'cn_noChange',
      '0,-1': 'cn_lessSameProfit',
      '-1,1': 'cn_moreMarginShort',
      '-1,0': 'cn_sameWorseMargin',
      '-1,-1': byMargin('cn_lessMarginShort', 'cn_lessSameMargin', 'cn_lessWorseMargin'),
    }[`${profit},${units}`];
    return { key, values: terms };
  };

  const renderConclusion = (record) => {
    const { key, muted, values } = conclusionKey(record);
    const sentence = (
      <Typography sx={{
        fontSize: '12px', lineHeight: 1.35, whiteSpace: 'normal', color: key && !muted ? '#37352f' : '#9b9a97',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif', letterSpacing: '-0.01em',
      }}>
        {key ? t(key, values) : '–'}
      </Typography>
    );
    const replicable = record.replicable;
    if (!replicable?.machines?.length) return sentence;
    // A swap that paid off here (and in most machines where it was tried): try it in more, right from here
    return (
      <Box>
        {sentence}
        <Typography
          component="button"
          onClick={(e) => { e.stopPropagation(); setReplicate({ record, anchorEl: e.currentTarget }); }}
          sx={{
            background: 'none', border: 'none', p: 0, mt: '2px', cursor: 'pointer', fontSize: '11.5px', color: '#2563eb',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {t('cn_tryIn', { count: replicable.machines.length })}
        </Typography>
      </Box>
    );
  };

  // Column widths configuration - centralized for easy adjustments
  const columnWidths = {
    key: 40,
    group: 164,
    route: 64,
    machineId: 60,
    products: 184,
    type: 49,
    date: 136,
    status: 92,
    tags: 160,
    conclusion: 220,
    result: 104
  };

  const tableScrollX = Object.values(columnWidths).reduce((sum, value) => sum + value, 0);

  // Function to get unit-level columns (for both main view and expanded rows)
  const getUnitColumns = React.useCallback((isNested = false) => {
    return [
      {
        title: t('group'),
        key: 'group',
        width: columnWidths.group,
        ellipsis: true,
        dataIndex: ['store', 'group'],
        sorter: !isNested, // Disable sorting in nested table
        sortIcon: !isNested ? renderSortIcon : undefined,
        sortOrder: !isNested && sortedInfo.columnKey === 'group' ? sortedInfo.order : null,
        render: (text) => (
          <span style={{
            fontSize: '12px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
            fontWeight: 400,
            color: '#37352f',
            letterSpacing: '-0.01em'
          }}>
            {text || '-'}
          </span>
        ),
        filterDropdown: !isNested ? createFilterDropdown('group', filterValues, t('group'), filtersLoading, t) : undefined,
        filteredValue: !isNested ? (filteredInfo.group || null) : undefined,
      },
      {
        title: t('route', { defaultValue: 'Route' }),
        key: 'route',
        width: columnWidths.route,
        ellipsis: true,
        render: (_, record) => renderRoutes(record.routes),
        filterDropdown: !isNested ? createFilterDropdown('route', filterValues, t('route', { defaultValue: 'Route' }), filtersLoading, t) : undefined,
        filteredValue: !isNested ? (filteredInfo.route || null) : undefined,
      },
      {
        title: t('machineId'),
        key: 'storeId',
        width: columnWidths.machineId,
        sorter: !isNested,
        sortIcon: !isNested ? renderSortIcon : undefined,
        sortOrder: !isNested && sortedInfo.columnKey === 'storeId' ? sortedInfo.order : null,
        render: (_, record) => renderClickableStoreId(record.store?.id, record),
        filterDropdown: !isNested ? createFilterDropdown('machineId', filterValues, t('machineId'), filtersLoading, t) : undefined,
        filteredValue: !isNested ? (filteredInfo.storeId || null) : undefined,
      },
      {
        title: t('products'),
        key: 'products',
        width: columnWidths.products,
        ellipsis: true,
        sorter: !isNested,
        sortIcon: !isNested ? renderSortIcon : undefined,
        sortOrder: !isNested && sortedInfo.columnKey === 'products' ? sortedInfo.order : null,
        render: (_, record) => formatProducts(record),
        filterDropdown: !isNested ? createFilterDropdown('products', filterValues, t('products'), filtersLoading, t) : undefined,
        filteredValue: !isNested ? (filteredInfo.products || null) : undefined,
      },
      {
        title: t('type'),
        dataIndex: 'type',
        key: 'type',
        width: columnWidths.type,
        align: 'center',
        sorter: !isNested,
        sortIcon: !isNested ? renderSortIcon : undefined,
        sortOrder: !isNested && sortedInfo.columnKey === 'type' ? sortedInfo.order : null,
        render: (experimentType) => (
          <ExperimentTypePill experimentType={experimentType} />
        ),
        filterDropdown: !isNested ? createFilterDropdown('type', filterValues, t('type'), filtersLoading, t) : undefined,
        filteredValue: !isNested ? (filteredInfo.type || null) : undefined,
      },
      {
        title: t('date'),
        key: 'dynamicDateRange',
        width: columnWidths.date,
        sorter: !isNested,
        sortIcon: !isNested ? renderSortIcon : undefined,
        sortOrder: !isNested && sortedInfo.columnKey === 'dynamicDateRange' ? sortedInfo.order : null,
        render: (_, record) => {
          const dateText = record.period ? formatDateRange(record.period.start, record.period.end) : 'No dates';
          return (
            <Typography sx={{
              fontSize: '13px',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
              fontWeight: 400,
              color: '#787774',
              letterSpacing: '-0.01em'
            }}>
              {dateText}
            </Typography>
          );
        },
        filterDropdown: !isNested ? (props) => (
          <TableFilterDropdown
            type="dateRange"
            columnName={t('date')}
            dateFilterMode={filteredInfo.dateFilterMode}
            onDateFilterModeChange={(mode) => {
              dateFilterModeRef.current = mode;
              setFilteredInfo(prev => ({ ...prev, dateFilterMode: mode }));
            }}
            {...props}
          />
        ) : undefined,
        filteredValue: !isNested ? (filteredInfo.dynamicDateRange || null) : undefined,
      },
      {
        title: t('status'),
        key: 'status',
        width: columnWidths.status,
        align: 'center',
        sorter: !isNested,
        sortIcon: !isNested ? renderSortIcon : undefined,
        sortOrder: !isNested && sortedInfo.columnKey === 'status' ? sortedInfo.order : null,
        render: (_, record) => {
          // Slot-change lineage from the nightly reconcile ([AUTO- markers in feedback):
          // a small warning with the per-carril history, e.g. an early-finish because the
          // product was removed from the machine. Plain operator feedback never triggers it.
          // Markers are machine bookkeeping — strip them so readers see plain sentences.
          const lineage = record.feedback && String(record.feedback).includes('[AUTO-')
            ? String(record.feedback)
                .split('\n')
                .map(line => line.replace(/^\[AUTO-[^\]]*\]\s*/, ''))
                .join('\n\n')
            : null;
          return (
            // ExperimentStatusIndicator sizes itself width:100% (the progress bar) — it must
            // keep the full cell width, so flex:1 here; the lineage icon only takes its 14px.
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: '100%' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {record.measurement ? (
                  <MeasurementStatus
                    measurement={record.measurement}
                    units={record.performance?.units}
                    baselineUnits={record.comparisons?.[tableBaselineMode]?.metadata?.hasData === false
                      ? null : record.comparisons?.[tableBaselineMode]?.baseline?.units}
                    zeroBaseline={record.comparisons?.[tableBaselineMode]?.metadata?.zeroBaseline}
                    lanes={(record.slots || []).map((slot) => slot.selectionId)}
                    machineId={record.store?.id}
                  >
                    <ExperimentStatusIndicator
                      status={record.status}
                      startDate={record.period?.start}
                      endDate={record.period?.end}
                      width={50}
                    />
                  </MeasurementStatus>
                ) : (
                  <ExperimentStatusIndicator
                    status={record.status}
                    startDate={record.period?.start}
                    endDate={record.period?.end}
                    width={50}
                  />
                )}
              </Box>
              {lineage && (
                <Tooltip
                  title={<span style={{ whiteSpace: 'pre-line' }}>{lineage}</span>}
                  arrow
                  placement="top"
                >
                  <InfoOutlined sx={{ fontSize: 14, color: '#f59e0b', cursor: 'help', flexShrink: 0 }} />
                </Tooltip>
              )}
            </Box>
          );
        },
        filterDropdown: !isNested ? createFilterDropdown('status', filterValues, t('status'), filtersLoading, t) : undefined,
        filteredValue: !isNested ? (filteredInfo.status || null) : undefined,
      },
      {
        title: t('tags'),
        key: 'tags',
        width: columnWidths.tags,
        dataIndex: 'tags',
        filterDropdown: !isNested ? renderTagFilterDropdown : undefined,
        filteredValue: !isNested ? (filteredInfo.tags || null) : undefined,
        render: (tags, record) => {
          // For nested tables, just display tags without edit functionality
          // Check editingTags first (updated after bulk operations)
          if (isNested) {
            const displayTags = editingTags[record.key || record.id] || tags;
            return displayTags && displayTags.length > 0 ? (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {displayTags.map((tag, index) => (
                  <Tag
                    key={`${tag.name}-${index}`}
                    color={tag.color || 'blue'}
                    style={{
                      fontSize: '11px',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                      fontWeight: 500,
                      margin: 0,
                    }}
                  >
                    {tag.name}
                  </Tag>
                ))}
              </Box>
            ) : '-';
          }

          // For main table, use full TagCell functionality
          return (
            <TagCell
              tags={tags}
              recordKey={record.key}
              experimentIds={record.isSlotGroup ? record.slotChildren?.map(c => c.id) : undefined}
              editingTags={editingTags}
              setEditingTags={setEditingTags}
              availableTags={availableTags}
              loadingTags={loadingTags}
              tagsFetchError={tagsFetchError}
              fetchTags={fetchTags}
              refreshFilters={refreshFilters}
              t={t}
              currentUser={currentUser}
              showToast={showToast}
            />
          );
        }
      },
      // What the experiment says, in words
      {
        title: (
          <Tooltip title={t('cn_headerHint')} arrow placement="top">
            <span style={{ cursor: 'help' }}>{t('cn_header')}</span>
          </Tooltip>
        ),
        key: 'conclusion',
        width: columnWidths.conclusion,
        render: (_, record) => renderConclusion(record),
      },
      // One number: did the experiment pay off
      {
        title: (
          <Tooltip title={t('res_profitHint')} arrow placement="top">
            <span style={{ cursor: 'help' }}>{t('grossProfit')}</span>
          </Tooltip>
        ),
        key: 'dynamicDeltaGrossProfit',
        width: columnWidths.result,
        align: 'center',
        sorter: !isNested,
        sortIcon: !isNested ? renderSortIcon : undefined,
        sortOrder: !isNested && sortedInfo.columnKey === 'dynamicDeltaGrossProfit' ? sortedInfo.order : null,
        render: (_, record) => renderResult(record),
      },
    ];
  }, [t, filteredInfo, sortedInfo, editingTags, filterValues, filtersLoading, renderTagFilterDropdown, availableTags, loadingTags, tagsFetchError, fetchTags, refreshFilters, currentUser, showToast, tableBaselineMode, navigate, slotExpandedKeys]);

  // Function to create grouped columns configuration for simplified grouped view
  const getGroupedColumns = (groupBy) => {
    const groupTitle = groupBy === 'group' ? t('group') :
                       groupBy === 'route' ? t('route', { defaultValue: 'Route' }) :
                       t('sector', { defaultValue: 'Sector' });

    return [
      {
        title: groupTitle,
        key: 'groupingLabel',
        dataIndex: 'groupingLabel',
        width: columnWidths.group + columnWidths.route, // Combined width
        ellipsis: true,
        sorter: true,
        sortIcon: renderSortIcon,
        sortOrder: sortedInfo.columnKey === 'groupingLabel' ? sortedInfo.order : null,
        render: (text) => (
          <span style={{
            fontSize: '13px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
            fontWeight: 500,
            color: '#37352f',
            letterSpacing: '-0.01em'
          }}>
            {text || '-'}
          </span>
        ),
      },
      {
        title: t('experiments', { defaultValue: 'Experiments' }),
        key: 'experimentCount',
        dataIndex: 'experimentCount',
        width: 120,
        sorter: true,
        sortIcon: renderSortIcon,
        sortOrder: sortedInfo.columnKey === 'experimentCount' ? sortedInfo.order : null,
        render: (count) => (
          <span style={{
            fontSize: '12px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
            fontWeight: 400,
            color: '#787774',
            letterSpacing: '-0.01em'
          }}>
            {count} {t('experiments', { defaultValue: 'experiments' }).toLowerCase()}
          </span>
        ),
      },
      {
        title: t('status'),
        key: 'status', // Use same key as unit view for consistent filtering
        dataIndex: 'status_summary', // But render the summary data
        width: columnWidths.status + 50, // Slightly wider for summary
        filterDropdown: createFilterDropdown('status', filterValues, t('status'), filtersLoading, t),
        filteredValue: filteredInfo.status || null,
        render: (statusSummary) => {
          if (!statusSummary) return '-';

          // Parse status summary (e.g., "5 running, 10 finished")
          const parts = statusSummary.split(', ');
          return (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {parts.map((part, index) => {
                const [count, status] = part.split(' ');
                const statusColor = status === 'running' ? '#10b981' :
                                   status === 'finished' ? '#6b7280' :
                                   status === 'scheduled' ? '#3b82f6' : '#6b7280';

                return (
                  <span
                    key={index}
                    style={{
                      fontSize: '11px',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                      padding: '2px 6px',
                      borderRadius: '3px',
                      backgroundColor: statusColor + '20',
                      color: statusColor,
                      fontWeight: 500
                    }}
                  >
                    {count} {status}
                  </span>
                );
              })}
            </Box>
          );
        },
      },
      {
        title: t('date'),
        key: 'dynamicDateRange', // Use same key as unit view for consistent filtering
        dataIndex: 'date_range', // But render the date_range data
        width: columnWidths.date,
        sorter: true,
        sortIcon: renderSortIcon,
        sortOrder: sortedInfo.columnKey === 'dynamicDateRange' ? sortedInfo.order : null,
        filterDropdown: (props) => (
          <TableFilterDropdown
            type="dateRange"
            columnName={t('date')}
            dateFilterMode={filteredInfo.dateFilterMode}
            onDateFilterModeChange={(mode) => {
              dateFilterModeRef.current = mode;
              setFilteredInfo(prev => ({ ...prev, dateFilterMode: mode }));
            }}
            {...props}
          />
        ),
        filteredValue: filteredInfo.dynamicDateRange || null,
        render: (dateRange) => {
          if (!dateRange || !dateRange.start || !dateRange.end) return '-';

          return (
            <Typography sx={{
              fontSize: '13px',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
              fontWeight: 400,
              color: '#787774',
              letterSpacing: '-0.01em'
            }}>
              {formatDateRange(dateRange.start, dateRange.end)}
            </Typography>
          );
        },
      },
      {
        title: (
          <Tooltip title={t('res_profitHint')} arrow placement="top">
            <span style={{ cursor: 'help' }}>{t('grossProfit')}</span>
          </Tooltip>
        ),
        key: 'delta_grossProfit',
        dataIndex: 'delta_grossProfit',
        width: columnWidths.result,
        align: 'center',
        sorter: true,
        sortIcon: renderSortIcon,
        sortOrder: sortedInfo.columnKey === 'delta_grossProfit' ? sortedInfo.order : null,
        render: (_, record) => renderGroupResult(record),
      },
    ];
  };

  // Table columns configuration - memoized for performance
  const columns = React.useMemo(() => {
    // Use grouped columns when in group or route mode
    if (viewMode === 'group' || viewMode === 'route' || viewMode === 'sector') {
      return getGroupedColumns(viewMode);
    }

    // Use regular columns for unit mode
    return getUnitColumns(false);
  }, [viewMode, getGroupedColumns, getUnitColumns]);
  

  // Prepare data with keys for Ant Design - memoized for performance
  const areDatesAligned = (dateA, dateB, toleranceDays = 2) => {
    if (!dateA || !dateB) return false;
    const a = new Date(dateA).getTime();
    const b = new Date(dateB).getTime();
    return Math.abs(a - b) <= toleranceDays * 24 * 60 * 60 * 1000;
  };

  const tableData = React.useMemo(() => {
    // In grouped mode, use groupingKey as the key
    if (viewMode === 'group' || viewMode === 'route' || viewMode === 'sector') {
      return data.map((item, index) => ({
        ...item,
        key: item.groupingKey || `group-${index}`
      }));
    }

    // In unit mode, check for slot experiments to group
    const withKeys = data.map((item, index) => ({
      ...item,
      key: item.id || `unit-${index}`
    }));

    // Group slot experiments by (machineId, original product)
    const slotGroups = new Map();
    const standalone = [];

    withKeys.forEach(item => {
      if (item.slots && item.slots.length > 0) {
        const groupKey = `${item.store?.id}-${item.product?.original}`;
        if (!slotGroups.has(groupKey)) {
          slotGroups.set(groupKey, []);
        }
        slotGroups.get(groupKey).push(item);
      } else {
        standalone.push(item);
      }
    });

    // For groups with 2+ experiments, sub-group by date alignment then create aggregated parent rows
    const result = [...standalone];
    slotGroups.forEach((experiments, groupKey) => {
      // Sub-group by date alignment (±2 days tolerance)
      const dateSubGroups = [];
      experiments.forEach(exp => {
        const matched = dateSubGroups.find(sg =>
          areDatesAligned(sg[0].period?.start, exp.period?.start) &&
          areDatesAligned(sg[0].period?.end, exp.period?.end)
        );
        if (matched) matched.push(exp);
        else dateSubGroups.push([exp]);
      });

      dateSubGroups.forEach((subGroup, subIdx) => {
        if (subGroup.length === 1) {
          // Single experiment with slots — wrap as expandable slot group
          const exp = subGroup[0];
          const parent = {
            ...exp,
            key: `slot-group-${groupKey}-${subIdx}`,
            isSlotGroup: true,
            slotChildren: subGroup,
          };
          result.push(parent);
        } else {
          // Aggregate performance metrics
          const aggPerformance = {
            units: subGroup.reduce((sum, e) => sum + (e.performance?.units || 0), 0),
            revenue: subGroup.reduce((sum, e) => sum + (e.performance?.revenue || 0), 0),
            grossProfit: subGroup.reduce((sum, e) => sum + (e.performance?.grossProfit || 0), 0),
            totalCost: subGroup.reduce((sum, e) => sum + (e.performance?.totalCost || 0), 0),
            avgPrice: subGroup.reduce((sum, e) => sum + (e.performance?.avgPrice || 0), 0) / subGroup.length,
          };

          // Aggregate comparisons across all periods
          const aggComparisons = {};
          const periods = Object.keys(subGroup[0].comparisons || {});
          periods.forEach(period => {
            const baselineUnits = subGroup.reduce((sum, e) => sum + (e.comparisons?.[period]?.baseline?.units || 0), 0);
            const baselineRevenue = subGroup.reduce((sum, e) => sum + (e.comparisons?.[period]?.baseline?.revenue || 0), 0);
            const baselineGrossProfit = subGroup.reduce((sum, e) => sum + (e.comparisons?.[period]?.baseline?.grossProfit || 0), 0);
            // No delta while any lane has no verdict yet (not started or still running)
            const noVerdict = subGroup.some(e => e.comparisons?.[period]?.delta?.units == null);
            aggComparisons[period] = {
              baseline: { units: baselineUnits, revenue: baselineRevenue, grossProfit: baselineGrossProfit },
              delta: noVerdict ? { units: null, revenue: null, grossProfit: null } : {
                units: aggPerformance.units - baselineUnits,
                revenue: aggPerformance.revenue - baselineRevenue,
                grossProfit: aggPerformance.grossProfit - baselineGrossProfit,
              },
              metadata: { hasData: subGroup.some(e => e.comparisons?.[period]?.metadata?.hasData !== false) },
            };
          });

          // Aggregate tags from all children (union, deduped by name-color)
          const aggTags = [];
          const seenTags = new Set();
          subGroup.forEach(exp => {
            (exp.tags || []).forEach(tag => {
              const tagKey = `${tag.name}-${tag.color}`;
              if (!seenTags.has(tagKey)) {
                seenTags.add(tagKey);
                aggTags.push(tag);
              }
            });
          });

          const parent = {
            ...subGroup[0],
            key: `slot-group-${groupKey}-${subIdx}`,
            isSlotGroup: true,
            slotChildren: subGroup,
            slots: subGroup.flatMap(e => e.slots || []),
            performance: aggPerformance,
            comparisons: aggComparisons,
            tags: aggTags,
            // The lane that most needs attention speaks for the group
            measurement: subGroup.map(e => e.measurement).filter(Boolean)
              .sort((a, b) => MEASUREMENT_SEVERITY.indexOf(a.state) - MEASUREMENT_SEVERITY.indexOf(b.state))[0],
            // Clear the new product since children may have different ones
            product: { ...subGroup[0].product, new: null },
            replicable: undefined,
          };
          result.push(parent);
        }
      });
    });

    return result;
  }, [data, viewMode]);

  // Memoize selected row keys for current page (uses tableData to include slot group keys)
  const currentPageSelectedRowKeys = React.useMemo(() => {
    const currentPageKeys = tableData.map(item => item.key || item.id);
    return selectedRowKeys.filter(key => currentPageKeys.includes(key));
  }, [selectedRowKeys, tableData]);

  // Calculate KPI aggregations from backend totals or fallback to local calculation
  const kpiTotals = React.useMemo(() => {
    // Delta over the baseline it is measured against (running experiments: the same days before), null without one
    const pctOf = (delta, baseline) => (baseline ? (delta / Math.abs(baseline)) * 100 : null);

    // Use backend totals if available
    if (totals && totals[tableBaselineMode]) {
      const periodTotals = totals[tableBaselineMode];
      
      // Check if we actually have any data
      const hasAnyData = 
        periodTotals.performance.units !== 0 || 
        periodTotals.performance.revenue !== 0 ||
        periodTotals.performance.grossProfit !== 0;
      
      return {
        // Performance values (totals during experiment period)
        totalUnits: hasAnyData ? periodTotals.performance.units : 0,
        totalRevenue: hasAnyData ? periodTotals.performance.revenue : 0,
        totalGrossProfit: hasAnyData ? periodTotals.performance.grossProfit : 0,
        
        // Delta values (difference from baseline)
        totalDeltaUnits: hasAnyData ? periodTotals.delta.units : 0,
        totalDeltaRevenue: hasAnyData ? periodTotals.delta.revenue : 0,
        totalDeltaGrossProfit: hasAnyData ? periodTotals.delta.grossProfit : 0,
        totalDeltaUnitsPct: hasAnyData ? pctOf(periodTotals.delta.units, periodTotals.baseline?.units) : null,
        totalDeltaRevenuePct: hasAnyData ? pctOf(periodTotals.delta.revenue, periodTotals.baseline?.revenue) : null,
        totalDeltaGrossProfitPct: hasAnyData ? pctOf(periodTotals.delta.grossProfit, periodTotals.baseline?.grossProfit) : null,

        hasData: true // Always show KPI boxes
      };
    }
    
    // Fallback to local calculation if totals not available
    let totalUnits = 0;
    let totalRevenue = 0;
    let totalGrossProfit = 0;
    let totalDeltaUnits = 0;
    let totalDeltaRevenue = 0;
    let totalDeltaGrossProfit = 0;
    let baselineUnits = 0;
    let baselineRevenue = 0;
    let baselineGrossProfit = 0;
    let validCount = 0;

    data.forEach(item => {
      // Get performance values
      if (item.performance) {
        totalUnits += item.performance.units || 0;
        totalRevenue += item.performance.revenue || 0;
        totalGrossProfit += item.performance.grossProfit || 0;
      }
      
      // Get delta values
      const comparison = item.comparisons?.[tableBaselineMode];
      if (comparison?.delta) {
        totalDeltaUnits += comparison.delta.units || 0;
        totalDeltaRevenue += comparison.delta.revenue || 0;
        totalDeltaGrossProfit += comparison.delta.grossProfit || 0;
        baselineUnits += comparison.baseline?.units || 0;
        baselineRevenue += comparison.baseline?.revenue || 0;
        baselineGrossProfit += comparison.baseline?.grossProfit || 0;
        validCount++;
      }
    });

    // Check if we have any data
    const hasAnyData = totalUnits !== 0 || totalRevenue !== 0 || totalGrossProfit !== 0;

    return {
      totalUnits: hasAnyData ? totalUnits : 0,
      totalRevenue: hasAnyData ? totalRevenue : 0,
      totalGrossProfit: hasAnyData ? totalGrossProfit : 0,
      totalDeltaUnits: hasAnyData ? totalDeltaUnits : 0,
      totalDeltaRevenue: hasAnyData ? totalDeltaRevenue : 0,
      totalDeltaGrossProfit: hasAnyData ? totalDeltaGrossProfit : 0,
      totalDeltaUnitsPct: hasAnyData ? pctOf(totalDeltaUnits, baselineUnits) : null,
      totalDeltaRevenuePct: hasAnyData ? pctOf(totalDeltaRevenue, baselineRevenue) : null,
      totalDeltaGrossProfitPct: hasAnyData ? pctOf(totalDeltaGrossProfit, baselineGrossProfit) : null,
      hasData: true // Always show KPI boxes
    };
  }, [data, tableBaselineMode, totals]);
  
  // Handle table change (including filters and sorting)
  // Track dateFilterMode in a ref so it's always current (not stale from closures)
  const dateFilterModeRef = React.useRef(filteredInfo.dateFilterMode);
  React.useEffect(() => {
    dateFilterModeRef.current = filteredInfo.dateFilterMode;
  }, [filteredInfo.dateFilterMode]);

  const handleTableChange = (pagination, filters, sorter) => {
    // Preserve dateFilterMode through antd filter cycles (antd doesn't track custom keys)
    filters.dateFilterMode = dateFilterModeRef.current || filteredInfo.dateFilterMode;
    setFilteredInfo(filters);
    setSortedInfo(sorter);

    // Trigger server-side filtering and sorting by calling parent's onFiltersChange
    if (onFiltersChange) {
      onFiltersChange(filters, filterData, sorter);
    }
  };
  

  // Pass initial filters to parent on mount
  React.useEffect(() => {
    if (onFiltersChange && filteredInfo) {
      onFiltersChange(filteredInfo, filterData, sortedInfo);
    }
  }, [onFiltersChange, filterData, sortedInfo]); // Include filterData and sortedInfo as dependencies
  
  // Remove a specific filter
  const handleRemoveFilter = (columnKey, value) => {
    const newFilteredInfo = { ...filteredInfo };
    
    // Special handling for date range filter
    if (columnKey === 'dynamicDateRange') {
      delete newFilteredInfo[columnKey];
    } else if (newFilteredInfo[columnKey]) {
      newFilteredInfo[columnKey] = newFilteredInfo[columnKey].filter(v => v !== value);
      if (newFilteredInfo[columnKey].length === 0) {
        delete newFilteredInfo[columnKey];
      }
    }
    
    setFilteredInfo(newFilteredInfo);
    
    // Trigger server-side filtering by calling parent's onFiltersChange
    if (onFiltersChange) {
      onFiltersChange(newFilteredInfo, filterData);
    }
  };

  // Fetch expanded details for grouped rows
  const fetchExpandedDetails = React.useCallback(async (experimentIds) => {
    try {
      const clientId = currentUser?.customer_id;
      const token = localStorage.getItem('access_token');

      // Map grouped column keys back to unit column keys for sorting
      const groupedToUnitMapping = {
        'delta_units': 'dynamicDelta',
        'delta_revenue': 'dynamicDeltaEuros',
        'delta_grossProfit': 'dynamicDeltaGrossProfit'
      };

      // Get the sort column for unit-level data
      let sortColumn = sortedInfo.columnKey;
      if (groupedToUnitMapping[sortColumn]) {
        sortColumn = groupedToUnitMapping[sortColumn];
      }

      const response = await axios.get(
        `${API_BASE_URL}/experiments/overview/group-details`,
        {
          params: {
            clientId,
            experimentIds: experimentIds,
            baseline: selectedBaseline,
            sortColumn: sortColumn,
            sortOrder: sortedInfo.order === 'ascend' ? 'asc' : 'desc'
          },
          paramsSerializer: params => {
            const searchParams = new URLSearchParams();
            searchParams.append('clientId', params.clientId);
            searchParams.append('baseline', params.baseline);
            if (params.sortColumn) {
              searchParams.append('sort[column]', params.sortColumn);
              searchParams.append('sort[order]', params.sortOrder);
              // For delta columns, also pass the baseline
              const deltaColumns = ['dynamicDelta', 'dynamicDeltaEuros', 'dynamicDeltaGrossProfit'];
              if (deltaColumns.includes(params.sortColumn)) {
                searchParams.append('sort[baseline]', params.baseline);
              }
            }
            params.experimentIds.forEach(id => searchParams.append('experimentIds', id));
            return searchParams.toString();
          },
          headers: createAuthHeader(token)
        }
      );

      return response.data.experiments || [];
    } catch (error) {
      console.error('Error fetching group details:', error);
      showToast(t('errorFetchingGroupDetails', { defaultValue: 'Error fetching group details' }), 'error');
      return [];
    }
  }, [currentUser, selectedBaseline, sortedInfo, showToast, t]);

  // Clear all filters
  const handleClearAllFilters = () => {
    setFilteredInfo({});
    
    // Trigger server-side filtering by calling parent's onFiltersChange
    if (onFiltersChange) {
      onFiltersChange({}, filterData);
    }
  };
  
  // Resolve selected row keys: expand slot group keys into real experiment IDs
  const resolveExperimentIds = React.useCallback((keys) => {
    const resolved = [];
    for (const key of keys) {
      const row = tableData.find(r => (r.key || r.id) === key);
      if (row?.isSlotGroup && row.slotChildren) {
        row.slotChildren.forEach(child => {
          const childId = child.id || child.key;
          if (childId && !resolved.includes(childId)) resolved.push(childId);
        });
      } else {
        if (!resolved.includes(key)) resolved.push(key);
      }
    }
    return resolved;
  }, [tableData]);

  const BoundTagRemoveContent = React.useCallback((props) => (
    <TagRemoveContent
      {...props}
      selectedRowKeys={resolveExperimentIds(selectedRowKeys)}
      isSelectAllActive={isSelectAllActive}
      allExperimentIds={allExperimentIds}
      currentUser={currentUser}
      t={t}
    />
  ), [selectedRowKeys, resolveExperimentIds, isSelectAllActive, allExperimentIds, currentUser, t]);

  // Handle bulk tag addition
  const handleBulkTagAdd = async (tagsOrName, tagColor) => {
    // Handle both single tag and array of tags
    const tagsToAdd = Array.isArray(tagsOrName) ? tagsOrName : [{ name: tagsOrName, color: tagColor || 'blue' }];
    
    try {
      const clientId = currentUser?.customer_id;
      const token = localStorage.getItem('access_token');
      
      // Use all experiment IDs if select-all is active, otherwise resolve selected rows
      // (slot group keys get expanded into their children's real experiment IDs)
      const experimentIds = isSelectAllActive && allExperimentIds.length > 0
        ? allExperimentIds
        : resolveExperimentIds(selectedRowKeys);

      // Use bulk endpoint with multiple IDs
      const response = await addTagsToExperiments(experimentIds, tagsToAdd, clientId, token);
      
      // Update with COMPLETE tag lists for each succeeded experiment
      if (response.results?.succeeded) {
        const updates = {};
        response.results.succeeded.forEach(({ experimentId, tags }) => {
          updates[experimentId] = tags; // Replace with full tag list from API
        });

        // Also update slot group parent keys so aggregate row shows tags
        const updatedIds = new Set(Object.keys(updates).map(Number));
        tableData.forEach(row => {
          if (row.isSlotGroup && row.slotChildren) {
            const childIds = row.slotChildren.map(c => c.id);
            if (childIds.some(id => updatedIds.has(id))) {
              // Merge all children's tags (from updates or existing editingTags/data)
              const allChildTags = [];
              const seen = new Set();
              childIds.forEach(cid => {
                const childTags = updates[cid] || [];
                childTags.forEach(tag => {
                  const tagKey = `${tag.name}-${tag.color}`;
                  if (!seen.has(tagKey)) {
                    seen.add(tagKey);
                    allChildTags.push(tag);
                  }
                });
              });
              updates[row.key] = allChildTags;
            }
          }
        });

        setEditingTags(prev => ({
          ...prev,
          ...updates
        }));

        // Show appropriate toast message
        if (response.success) {
          showToast(t('tagsAddedSuccessfully'), 'success');
        } else if (response.results.failed?.length > 0) {
          showToast(
            t('tagsAddedPartially', {
              succeeded: response.results.succeeded.length,
              failed: response.results.failed.length
            }),
            'warning'
          );
        }

        // Update tag filters in background (prevents table refresh)
        refreshFilters();
      }
    } catch (error) {
      console.error('Error adding tags:', error);
      showToast(t('errorAddingTags'), 'error');
    }
    
    // Close popover first, then clear selection
    setBulkTagPopoverOpen(false);
    // Delay clearing selection to allow popover to close properly
    setTimeout(() => {
      setSelectedRowKeys([]);
      setIsSelectAllActive(false);
    }, 100);
  };
  
  // Handle bulk tag removal
  const handleBulkTagRemove = async (tagsToRemove) => {
    try {
      const clientId = currentUser?.customer_id;
      const token = localStorage.getItem('access_token');
      
      // Use all experiment IDs if select-all is active, otherwise resolve selected rows
      // (slot group keys get expanded into their children's real experiment IDs)
      const experimentIds = isSelectAllActive && allExperimentIds.length > 0
        ? allExperimentIds
        : resolveExperimentIds(selectedRowKeys);

      // Use bulk endpoint with multiple IDs
      const response = await removeTagsFromExperiments(experimentIds, tagsToRemove, clientId, token);

      // Update with COMPLETE tag lists for each succeeded experiment
      if (response.results?.succeeded) {
        const updates = {};
        response.results.succeeded.forEach(({ experimentId, tags }) => {
          updates[experimentId] = tags; // Replace with full tag list from API
        });

        // Also update slot group parent keys so aggregate row shows tags
        const updatedIds = new Set(Object.keys(updates).map(Number));
        tableData.forEach(row => {
          if (row.isSlotGroup && row.slotChildren) {
            const childIds = row.slotChildren.map(c => c.id);
            if (childIds.some(id => updatedIds.has(id))) {
              const allChildTags = [];
              const seen = new Set();
              childIds.forEach(cid => {
                const childTags = updates[cid] || [];
                childTags.forEach(tag => {
                  const tagKey = `${tag.name}-${tag.color}`;
                  if (!seen.has(tagKey)) {
                    seen.add(tagKey);
                    allChildTags.push(tag);
                  }
                });
              });
              updates[row.key] = allChildTags;
            }
          }
        });

        setEditingTags(prev => ({
          ...prev,
          ...updates
        }));

        // Show appropriate toast message
        if (response.success) {
          showToast(t('tagsRemovedSuccessfully'), 'success');
        } else if (response.results.failed?.length > 0) {
          showToast(
            t('tagsRemovedPartially', {
              succeeded: response.results.succeeded.length,
              failed: response.results.failed.length
            }),
            'warning'
          );
        }

        // Update tag filters in background (prevents table refresh)
        refreshFilters();
      }
    } catch (error) {
      console.error('Error removing tags:', error);
      showToast(t('errorRemovingTags'), 'error');
    }
    
    // Close popover first, then clear selection
    setBulkRemoveTagsOpen(false);
    // Delay clearing selection to allow popover to close properly
    setTimeout(() => {
      setSelectedRowKeys([]);
      setIsSelectAllActive(false);
    }, 100);
  };
  
  // Column configs for filter pills
  const columnConfigs = {
    group: { title: t('group') },
    location: { title: t('location') },
    storeId: { title: t('machine') },
    route: { 
      title: t('route', { defaultValue: 'Route' }),
      valueFormatter: (value) => {
        const routes = Array.isArray(filterData?.routes) ? filterData.routes : [];
        const match = routes.find((route) => String(route.id) === String(value));
        return match?.name || match?.code || value;
      }
    },
    type: { title: t('type') },
    products: { title: t('products') },
    status: { title: t('status') },
    tags: { title: t('tags') },
    dynamicDateRange: { title: t('experimentPeriod') }
  };

  return (
    <Box
      data-testid="experiments-table"
      sx={{
      backgroundColor: 'white',
      borderRadius: '12px',
      border: `1px solid ${colors.border.light}`,
      overflow: 'hidden'
    }}>
      {/* Hidden view mode for testing */}
      <div data-testid="view-mode" style={{ display: 'none' }}>{viewMode}</div>

      {/* Header */}
      <Box sx={{
        pt: responsiveSpacing.lg,
        pb: 0.5, // Minimal bottom padding
        px: responsiveSpacing.sm, // Match the filter pills padding
        backgroundColor: colors.background.surface
      }}>
        {/* Title and Time Filter Row */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          rowGap: 1,
          mb: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flex: '1 1 auto', minWidth: 0 }}>
            <Typography sx={{ 
              fontSize: '20px', 
              color: '#37352f',
              fontWeight: 600,
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif'
            }}>
              {t('experimentsResults')}
            </Typography>
            <Box sx={{ 
              width: '4px', 
              height: '4px', 
              borderRadius: '50%', 
              backgroundColor: '#787774',
              alignSelf: 'center'
            }} />
            <Typography sx={{
              fontSize: '16px',
              color: '#787774',
              fontWeight: 500,
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif'
            }}>
              {displayExperimentsCount} {t('experiments_word_plural')}
            </Typography>
            {filtersError && (
              <Tooltip title={t('filtersLoadingError')} arrow placement="top">
                <InfoOutlined 
                  sx={{ 
                    fontSize: '14px',
                    color: '#d9534f',
                    cursor: 'help',
                    ml: 0.5
                  }} 
                />
              </Tooltip>
            )}
          </Box>
          
          {/* View Mode Toggle and Baseline Period Selector */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flexShrink: 0, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
            {/* View Mode Selector */}
            {onViewModeChange && (
              <div data-testid="view-mode-toggle">
                <Select
                  value={viewMode}
                  onChange={(e) => onViewModeChange(e.target.value)}
                  size="small"
                  sx={{
                    fontSize: '12px',
                    minWidth: 120,
                    '& .MuiSelect-select': {
                      py: 0.5,
                      px: 2
                    }
                  }}
                >
                  <MenuItem value="unit">{t('unitLevel')}</MenuItem>
                  <MenuItem value="group">{t('byGroup')}</MenuItem>
                  <MenuItem value="route">{t('byRoute')}</MenuItem>
                  <MenuItem value="sector">{t('bySector')}</MenuItem>
                </Select>
              </div>
            )}

            {/* Baseline Period Selector */}
            <ButtonGroup size="small" variant="outlined" sx={{ flex: { xs: '1 1 100%', sm: '0 1 auto' }, '& .MuiButton-root': { flex: { xs: 1, sm: 'none' }, fontSize: { xs: '10px', sm: '12px' }, px: { xs: 0.5, sm: 2 }, lineHeight: 1.15, whiteSpace: 'normal' } }}>
              <Button
                variant={tableBaselineMode === 'historical' ? 'contained' : 'outlined'}
                onClick={() => handleBaselineChange('historical')}
                sx={{ 
                  textTransform: 'none', 
                  fontSize: '12px',
                  py: 0.5,
                  px: 2
                }}
              >
                {t('historical')}
              </Button>
              <Button
                variant={tableBaselineMode === 'year_ago' ? 'contained' : 'outlined'}
                onClick={() => handleBaselineChange('year_ago')}
                sx={{ 
                  textTransform: 'none', 
                  fontSize: '12px',
                  py: 0.5,
                  px: 2
                }}
              >
                {t('yearAgo')}
              </Button>
              {/* <Button
                variant={tableBaselineMode === '4weeks' ? 'contained' : 'outlined'}
                onClick={() => handleBaselineChange('4weeks')}
                sx={{ 
                  textTransform: 'none', 
                  fontSize: '12px',
                  py: 0.5,
                  px: 2
                }}
              >
                {t('fourWeeksBefore')}
              </Button> */}
            </ButtonGroup>
            <Tooltip 
              title={
                <Box sx={{ p: 1 }}>
                  <Typography sx={{ fontWeight: 600, mb: 1, fontSize: '13px' }}>
                    {t('baselineInfoTitle')}
                  </Typography>
                  <Typography sx={{ mb: 1, fontSize: '12px' }}>
                    <strong>{t('historical')}:</strong> {t('baselineInfoHistorical')}
                  </Typography>
                  <Typography sx={{ fontSize: '12px' }}>
                    <strong>{t('yearAgo')}:</strong> {t('baselineInfoYearAgo')}
                  </Typography>
                  {/* <Typography sx={{ mb: 1, fontSize: '12px' }}>
                    <strong>{t('fourWeeksBefore')}:</strong> {t('baselineInfo4Weeks')}
                  </Typography> */}
                </Box>
              }
              placement="bottom-end"
              arrow
            >
              <InfoOutlined 
                sx={{ 
                  fontSize: '16px', 
                  color: '#8c8c8c',
                  cursor: 'help',
                  '&:hover': {
                    color: '#595959'
                  }
                }} 
              />
            </Tooltip>
          </Box>
        </Box>

        {/* KPI Cards */}
        {kpiTotals.hasData && (
          <Box sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            mt: 3,
            mb: 2
          }}>
            {/* Total Units Card */}
            <KPIBox
              title={t('totalUnits')}
              value={kpiTotals.totalUnits}
              delta={kpiTotals.totalDeltaUnits}
              percentage={kpiTotals.totalDeltaUnitsPct}
              type="units"
              showComparisonHint={showKPIComparisonHint}
            />

            {/* Total Revenue Card */}
            <KPIBox
              title={t('totalRevenue')}
              value={kpiTotals.totalRevenue}
              delta={kpiTotals.totalDeltaRevenue}
              percentage={kpiTotals.totalDeltaRevenuePct}
              type="currency"
              showComparisonHint={showKPIComparisonHint}
            />

            {/* Total Gross Profit Card */}
            <KPIBox
              title={t('totalGrossProfit')}
              value={kpiTotals.totalGrossProfit}
              delta={kpiTotals.totalDeltaGrossProfit}
              percentage={kpiTotals.totalDeltaGrossProfitPct}
              type="currency"
              showComparisonHint={showKPIComparisonHint}
            />
          </Box>
        )}
      </Box>
      
      {/* Unified Filter/Selection Bar - swaps between FilterPills and SelectionActionBar */}
      <Box sx={{ 
        px: responsiveSpacing.sm,
        py: selectedRowKeys.length > 0 ? 1.5 : 1,
        minHeight: selectedRowKeys.length > 0 ? '56px' : '36px',
        display: 'flex',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: selectedRowKeys.length > 0 ? 100 : 10,
        backgroundColor: 'white',
        transition: 'all 0.2s ease',
        boxShadow: (selectedRowKeys.length > 0 || isScrolled) ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
        borderBottom: selectedRowKeys.length > 0 ? '2px solid #1890ff' : 'none',
        marginTop: selectedRowKeys.length > 0 ? '-1px' : 0
      }}>
        {selectedRowKeys.length > 0 ? (
          <Box sx={{ 
            animation: 'slideIn 0.2s ease-out',
            '@keyframes slideIn': {
              from: { opacity: 0, transform: 'translateX(-10px)' },
              to: { opacity: 1, transform: 'translateX(0)' }
            }
          }}>
            <SelectionActionBar
              selectedRowsCount={selectedRowKeys.length}
              totalCount={totalCount}
              isSelectAllActive={isSelectAllActive}
              onBulkTagClick={() => setBulkTagPopoverOpen(true)}
              onBulkRemoveTagClick={() => setBulkRemoveTagsOpen(true)}
              bulkTagPopoverOpen={bulkTagPopoverOpen}
              setBulkTagPopoverOpen={setBulkTagPopoverOpen}
              bulkRemoveTagsOpen={bulkRemoveTagsOpen}
              setBulkRemoveTagsOpen={setBulkRemoveTagsOpen}
              TagAddContent={BoundTagAddContent}
              TagRemoveContent={BoundTagRemoveContent}
              onBulkTagAdd={handleBulkTagAdd}
              onBulkTagRemove={handleBulkTagRemove}
              availableTags={availableTags}
              loadingTags={loadingTags}
              tagsFetchError={tagsFetchError}
            />
          </Box>
        ) : (
          <Box sx={{ 
            animation: 'fadeIn 0.2s ease-out',
            '@keyframes fadeIn': {
              from: { opacity: 0 },
              to: { opacity: 1 }
            },
            width: '100%'
          }}>
            <FilterPills
              filters={filteredInfo}
              onRemoveFilter={handleRemoveFilter}
              onClearAll={handleClearAllFilters}
              columnConfigs={columnConfigs}
            />
          </Box>
        )}
      </Box>

      {/* Table */}
      <Box sx={{ 
        px: responsiveSpacing.sm,
        '& .ant-table': {
          backgroundColor: 'transparent',
        },
        '& .ant-table-thead > tr > th': {
          backgroundColor: 'transparent',
          borderBottom: '1px solid #e7e5e4',
          fontSize: '12px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
          fontWeight: 500,
          color: '#787774',
          textTransform: 'none',
          letterSpacing: '-0.01em',
          padding: '12px 8px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        '& .ant-table-tbody > tr > td': {
          borderBottom: '1px solid #f0efee',
          padding: '12px 8px',
        },
        '& .ant-table-tbody > tr:hover > td': {
          backgroundColor: '#fbfbfa',
        },
        '& .ant-table-tbody > tr.slot-group-row:hover > td': {
          backgroundColor: '#F8FAFC',
        },
        '& .ant-table-tbody > tr.slot-group-row:hover .slot-expand-chevron': {
          color: '#3B82F6 !important',
        },
        '& .ant-table-expand-icon-col': {
          width: '0 !important',
          minWidth: '0 !important',
          padding: '0 !important',
          border: 'none !important',
        },
        '& .ant-table-row-expand-icon-cell': {
          width: '0 !important',
          minWidth: '0 !important',
          padding: '0 !important',
          border: 'none !important',
        },
        '& .ant-table-expanded-row > td.ant-table-cell': {
          padding: '0 !important',
        },
        '& .ant-pagination': {
          display: 'none', // We'll use MUI pagination instead
        },
        // Make checkbox borders darker
        '& .ant-checkbox-inner': {
          borderColor: '#666666 !important',
        },
        '& .ant-checkbox:hover .ant-checkbox-inner': {
          borderColor: '#333333 !important',
        },
        // Make expanded row background white
        '& .ant-table-expanded-row > td': {
          backgroundColor: '#FFFFFF !important',
        },
        '& .ant-table-expanded-row-fixed': {
          backgroundColor: '#FFFFFF !important',
        }
      }}>
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <Table
            columns={columns}
            dataSource={tableData}
            loading={loading}
            pagination={false}
            size="small"
            scroll={{ x: tableScrollX }}
            tableLayout="fixed"
            onChange={handleTableChange}
            locale={{
              emptyText: viewMode === 'group' ? 'No groups found' :
                         viewMode === 'route' ? 'No routes found' :
                         viewMode === 'sector' ? 'No sectors found' :
                         'No data'
            }}
            onRow={(record) => ({
              onMouseEnter: () => setHoveredRowKey(record.key || record.id),
              onMouseLeave: () => setHoveredRowKey(null),
              ...(record.isSlotGroup ? {
                onClick: (e) => {
                  // Don't toggle if clicking on a checkbox, button, link, or interactive element
                  if (e.target.closest('button, a, input, .ant-checkbox, .ant-tag')) return;
                  setSlotExpandedKeys(prev => {
                    const key = record.key;
                    return prev.includes(key)
                      ? prev.filter(k => k !== key)
                      : [...prev, key];
                  });
                },
                className: 'slot-group-row',
                style: { cursor: 'pointer' },
              } : {}),
              ...((viewMode === 'group' || viewMode === 'route' || viewMode === 'sector') && record.groupingKey ? {
                onClick: (e) => {
                  if (e.target.closest('button, a, input, .ant-checkbox, .ant-tag')) return;
                  const key = record.groupingKey;
                  setExpandedRowKeys(prev =>
                    prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
                  );
                  if (!expandedRowData[record.groupingKey]) {
                    fetchExpandedDetails(record.experimentIds).then(details => {
                      setExpandedRowData(prev => ({ ...prev, [record.groupingKey]: details }));
                    });
                  }
                },
                style: { cursor: 'pointer' },
              } : {}),
            })}
            rowSelection={viewMode === 'unit' ? {
            columnWidth: columnWidths.key, // Reduced from default 60px
            selectedRowKeys: currentPageSelectedRowKeys,
            renderCell: (checked, record, index, originNode) => {
              const isHovered = hoveredRowKey === (record.key || record.id);
              const isSelected = selectedRowKeys.includes(record.key || record.id);
              const shouldBeVisible = isHovered || isSelected;
              
              return (
                <div style={{ 
                  opacity: shouldBeVisible ? 1 : 0,
                  transition: 'opacity 200ms ease',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {originNode}
                </div>
              );
            },
            onChange: (newSelectedRowKeys) => {
              // Get keys for current page items (use tableData to include slot group keys)
              const currentPageKeys = tableData.map(item => item.key || item.id);

              // Preserve selections from other pages
              setSelectedRowKeys(prevKeys => {
                // Remove all current page keys from previous selection
                const otherPageKeys = prevKeys.filter(key => !currentPageKeys.includes(key));
                // Add the new selection from current page
                return [...otherPageKeys, ...newSelectedRowKeys];
              });

              // Reset select-all flag if selection changes via individual rows
              if (newSelectedRowKeys.length !== tableData.length && isSelectAllActive) {
                setIsSelectAllActive(false);
              }
            },
            onSelectAll: (selected, selectedRows, changeRows) => {
              // Track whether select-all checkbox was used
              setIsSelectAllActive(selected);
              // If deselecting all, clear the flag
              if (!selected) {
                setSelectedRowKeys([]);
              }
            }
            } : undefined}
            expandable={(viewMode === 'group' || viewMode === 'route' || viewMode === 'sector') ? {
              expandedRowKeys,
              expandIcon: () => null,
              onExpandedRowsChange: setExpandedRowKeys,
              onExpand: async (expanded, record) => {
                if (expanded && !expandedRowData[record.groupingKey]) {
                  // Fetch details if not cached
                  const details = await fetchExpandedDetails(record.experimentIds);
                  setExpandedRowData(prev => ({
                    ...prev,
                    [record.groupingKey]: details
                  }));
                }
              },
              expandedRowRender: (record) => {
                const detailsData = expandedRowData[record.groupingKey] || [];
                const isLoading = !expandedRowData[record.groupingKey] && expandedRowKeys.includes(record.groupingKey);

                if (isLoading) {
                  return (
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      py: 3,
                      backgroundColor: '#FFFFFF'
                    }}>
                      <Spin size="small" />
                    </Box>
                  );
                }

                if (detailsData.length === 0) {
                  return (
                    <Box sx={{
                      textAlign: 'center',
                      py: 2,
                      color: '#787774',
                      backgroundColor: '#FFFFFF'
                    }}>
                      No experiments found
                    </Box>
                  );
                }

                // Use the exact same columns as unit level view, but with nested flag
                const unitColumns = getUnitColumns(true);

                // Prepare nested table data
                const nestedTableData = detailsData.map((item, index) => ({
                  ...item,
                  key: item.id || `nested-${record.groupingKey}-${index}`
                }));

                return (
                  <Box
                    sx={{
                      padding: '16px',
                      backgroundColor: '#FFFFFF',
                    }}
                  >
                    {/* Wrapper with rounded border to highlight the table */}
                    <Box
                      sx={{
                        border: '1px solid #E5E5E5',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        padding: '12px',
                      }}
                    >
                      <Table
                        columns={unitColumns}
                        dataSource={nestedTableData}
                        pagination={false}
                        size="small"
                        showHeader={true}
                        scroll={{ x: tableScrollX }}
                        tableLayout="fixed"
                        rowKey="key"
                      />
                    </Box>
                  </Box>
                );
              },
              rowExpandable: (record) => true,
            } : (tableData.some(item => item.isSlotGroup)) ? {
              expandedRowKeys: slotExpandedKeys,
              expandIcon: () => null,
              expandedRowRender: (record) => {
                if (!record.isSlotGroup) return null;
                const unitColumns = getUnitColumns(true);
                // Insert Carriles column after products column
                const productsIdx = unitColumns.findIndex(c => c.key === 'products');
                const carrilesColumn = {
                  title: t('slots', { defaultValue: 'Carriles' }),
                  key: 'carriles',
                  width: 70,
                  ellipsis: true,
                  render: (_, rec) => {
                    const ids = rec._slotIds;
                    if (!ids) return '-';
                    return (
                      <Tooltip title={ids} enterDelay={500} arrow>
                        <span style={{
                          fontSize: '11px',
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                          color: '#787774',
                        }}>
                          {ids}
                        </span>
                      </Tooltip>
                    );
                  },
                };
                const slotColumns = [...unitColumns];
                slotColumns.splice(productsIdx + 1, 0, carrilesColumn);
                // Strip slots from children (no badge in product column), stash IDs in _slotIds
                const nestedData = record.slotChildren.map((item, index) => ({
                  ...item,
                  slots: undefined,
                  _slotIds: item.slots?.map(s => s.selectionId).join(', ') || null,
                  key: item.id || `slot-child-${index}`
                }));
                return (
                  <Box sx={{ padding: '16px', backgroundColor: '#FFFFFF' }}>
                    <Box sx={{ border: '1px solid #E5E5E5', borderRadius: '8px', overflow: 'hidden', padding: '12px' }}>
                      <Table
                        columns={slotColumns}
                        dataSource={nestedData}
                        pagination={false}
                        size="small"
                        showHeader={true}
                        scroll={{ x: tableScrollX + 70 }}
                        tableLayout="fixed"
                        rowKey="key"
                      />
                    </Box>
                  </Box>
                );
              },
              rowExpandable: (record) => record.isSlotGroup,
            } : undefined}
          />
        </Box>
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ 
          p: responsiveSpacing.md,
          display: 'flex',
          justifyContent: 'center',
          borderTop: `1px solid ${colors.border.light}`,
          backgroundColor: colors.background.surface
        }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(event, page) => {
              // Keep select-all active when changing pages
              if (onPageChange) {
                onPageChange(page);
              }
            }}
            color="primary"
            size="small"
            showFirstButton
            showLastButton
            sx={{
              '& .MuiPaginationItem-root': {
                fontSize: '13px',
                fontWeight: 500,
              }
            }}
          />
        </Box>
      )}

      {replicate && (
        <ReplicatePopover
          record={replicate.record}
          anchorEl={replicate.anchorEl}
          customerId={currentUser?.customer_id}
          baseline={tableBaselineMode}
          onClose={() => setReplicate(null)}
        />
      )}
    </Box>
  );
};

// Add display name for React DevTools
ExperimentsTable.displayName = 'ExperimentsTable';

export default ExperimentsTable;
